import { useEffect, useState, useMemo, useRef, memo, lazy, Suspense } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import MultiSelect from "@/components/ui/multi-select";
// R268rrrr · Feb 2026 — Lazy-load dashboard-only panels so the ~180KB
// of recharts + analytics code doesn't ship with the Leads list route.
// Mounts only when the user picks the Dashboard tab, wrapped in
// Suspense with a compact skeleton.
const LeadsAnalyticsPanel = lazy(() => import("@/components/leads/LeadsAnalyticsPanel"));
import LeadsQuickAddFab from "@/components/leads/LeadsQuickAddFab";
import LeadsCardsView from "@/components/leads/LeadsCardsView";
import LeadsPipelineView from "@/components/leads/LeadsPipelineView";
import LeadsGlobalSearch from "@/components/leads/LeadsGlobalSearch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { Briefcase, Plus, Search, ArrowRight, Trash2, Bookmark, Filter as FilterIcon, Save, X, Tag as TagIcon, Pencil, Users, MessageCircle, Star, Flame, Upload, Sparkles, Settings2, Mail, ChevronDown, History, FileSpreadsheet, Loader2, LayoutGrid } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import LeadWaChatDialog from "@/components/wa/LeadWaChatDialog";
import { AddToSegmentDialog, BulkWhatsAppDialog, BulkEmailDialog } from "./LeadsBulkDialogs";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import EmailsTab from "@/components/EmailsTab";
import AdvancedTable from "@/components/table/AdvancedTable";
import ResearchDialog from "@/components/ResearchDialog";
import CallButton from "@/components/CallButton";
import CallStatusDialog from "@/components/CallStatusDialog";
import HotDealsWidget from "@/components/HotDealsWidget";
import QuickContactActions from "@/components/QuickContactActions";
import { LeadHistoryPanel } from "@/components/leads/LeadHistoryPanel";
import LeadStatusManagerDialog from "@/components/leads/LeadStatusManagerDialog";
import IndustryManagerDialog from "@/components/leads/IndustryManagerDialog";
import { LeadCallHistoryDialog, TemperaturePill } from "@/components/leads/LeadCallHistoryDialog";
import { Phone } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "proposal", "won", "lost"];
// R68 · Legacy static list retained only as a seed fallback. Actual list
// is loaded from /workspaces/{ws}/lead-statuses so each workspace can
// define its own funnel vocabulary.
const FALLBACK_STATUSES = STATUSES;
const STATUS_COLOR = {
  new: "chip-internal",
  contacted: "chip-status",
  qualified: "chip-pending",
  proposal: "chip-pending",
  won: "chip-approved",
  lost: "chip-rejected",
};
const SOURCES = ["website", "referral", "outbound", "social", "event", "partner", "other"];

export default function Leads() {
  const { activeWorkspaceId, memberships } = useAuth();
  const { can } = useWorkspacePerms();
  const _membership = memberships?.find((m) => m.workspace_id === activeWorkspaceId);
  const canManageLeads = ["owner", "admin", "manager", "workspace_owner", "workspace_admin"].includes(_membership?.role) || can("lead.manage");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  // R267f · Feb 2026 — Cursor pagination state. `nextCursor` is the
  // opaque token from the backend used to fetch the next page; the
  // FE never inspects its shape. `loadingMore` guards double-load
  // races when the IntersectionObserver fires while a fetch is in
  // flight.
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // R267e · Feb 2026 — Dashboard vs Leads subtab. Default "leads" so
  // the huge Aquarius Global workspace lands directly on the filterable
  // table without paying for WinLoss / funnel / HotDeals aggregations.
  // Persisted in the URL so bookmarks stay honest.
  const [searchParams, setSearchParams] = useSearchParams();
  const _rawView = searchParams.get("view");
  const view = _rawView === "dashboard" ? "dashboard" : _rawView === "pipeline" ? "pipeline" : "leads";
  const setView = (v) => setSearchParams((p) => {
    const n = new URLSearchParams(p);
    if (v === "leads") n.delete("view"); else n.set("view", v);
    return n;
  });
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  // Feb 2026 · segmentation filters
  const [filterSource, setFilterSource] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("");
  const [filterSubIndustry, setFilterSubIndustry] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterOwner, setFilterOwner] = useState(""); // R52 · assignee filter
  const [filterOnDnd, setFilterOnDnd] = useState(false); // Feb 2026 · DND-only chip
  // R268nnnn · Feb 2026 — Multi-value filters (Company / Designation /
  // Country / City). Sourced from /leads/filter-options which returns
  // the workspace's actual distinct values sorted by frequency.
  // Persisted per user × workspace in localStorage so refresh keeps
  // the PM's slicing preferences.
  const _lsMulti = (k) => `leads-${k}:${activeWorkspaceId || "_"}`;
  const _readMulti = (k) => {
    try {
      const raw = localStorage.getItem(_lsMulti(k));
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  };
  // R268xxxx · Feb 2026 — Inline Owner / Temperature edit was removed
  // (see Owner column comment). Kept the state placeholders as no-ops
  // in case future callers reference `activeRowFor` — they resolve to
  // "" which the memo comparator treats as unchanged, so 0 overhead.
  const ownerEditingId = null;
  const tempEditingId = null;
  const [filterCompanies, setFilterCompanies] = useState(() => _readMulti("companies"));
  const [filterDesignations, setFilterDesignations] = useState(() => _readMulti("designations"));
  const [filterCountries, setFilterCountries] = useState(() => _readMulti("countries"));
  const [filterCities, setFilterCities] = useState(() => _readMulti("cities"));
  const [filterOptions, setFilterOptions] = useState({ companies: [], designations: [], countries: [], cities: [] });
  useEffect(() => {
    try { localStorage.setItem(_lsMulti("companies"), JSON.stringify(filterCompanies || [])); } catch { /* ignore */ }
    // eslint-disable-next-line
  }, [filterCompanies, activeWorkspaceId]);
  useEffect(() => {
    try { localStorage.setItem(_lsMulti("designations"), JSON.stringify(filterDesignations || [])); } catch { /* ignore */ }
    // eslint-disable-next-line
  }, [filterDesignations, activeWorkspaceId]);
  useEffect(() => {
    try { localStorage.setItem(_lsMulti("countries"), JSON.stringify(filterCountries || [])); } catch { /* ignore */ }
    // eslint-disable-next-line
  }, [filterCountries, activeWorkspaceId]);
  useEffect(() => {
    try { localStorage.setItem(_lsMulti("cities"), JSON.stringify(filterCities || [])); } catch { /* ignore */ }
    // eslint-disable-next-line
  }, [filterCities, activeWorkspaceId]);
  const [wsMembers, setWsMembers] = useState([]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/members`)
      .then((r) => setWsMembers((r.data || []).filter(m => m.status !== "removed")))
      .catch(() => setWsMembers([]));
  }, [activeWorkspaceId]);
  const [editing, setEditing] = useState(null);
  const [waLead, setWaLead] = useState(null);  // R44 · WA chat popup
  const [callLogLead, setCallLogLead] = useState(null);  // R48 · Call log dialog
  const [enqHistoryLead, setEnqHistoryLead] = useState(null);  // Feb 2026 · Enquiry history drawer
  const navigate = useNavigate();
  const [industriesCatalog, setIndustriesCatalog] = useState({ industries: [], sub_industries: {} });
  const [tagCatalog, setTagCatalog] = useState([]);   // [{name, count}]
  const [segments, setSegments] = useState([]);
  const [savingSegment, setSavingSegment] = useState(false);
  const [showSaveSegment, setShowSaveSegment] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // R293 · Oct 2026 · Leads list view mode (toolbar toggle).
  //   table   → AdvancedTable (full columns, bulk edit, virtualized)
  //   cards   → Big card grid (phone-friendly, 1 col <sm, 2 col sm-lg, 3 col xl)
  //   compact → Dense card grid (more rows per screen; useful on tablet)
  // Persisted per-user via localStorage. Phones auto-default to `cards`
  // the first time they land on the page.
  const [leadsListMode, _setLeadsListMode] = useState(() => {
    try {
      const v = localStorage.getItem("leadsListMode");
      if (v === "table" || v === "cards" || v === "compact") return v;
    } catch (e) { void e; }
    // Phone-first default
    if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)").matches) return "cards";
    return "table";
  });
  const setLeadsListMode = (v) => {
    _setLeadsListMode(v);
    try { localStorage.setItem("leadsListMode", v); } catch (e) { void e; }
  };
  // R293 · Oct 2026 · Mobile filter drawer open state.
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showBulkTransfer, setShowBulkTransfer] = useState(false);   // R268xxxx · bulk owner reassign
  const [showAddToSegment, setShowAddToSegment] = useState(false);
  const [showBulkWa, setShowBulkWa] = useState(false);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  // R68 · Workspace-configurable lead statuses
  const [leadStatuses, setLeadStatuses] = useState([]);  // [{ id, key, label, color, order, is_terminal_won, is_terminal_lost, archived_at }]
  const [showStatusManager, setShowStatusManager] = useState(false);
  // Oct 2026 · Inline Industry catalog manager (add workspace-only
  // industries + sub-industries, review unmatched values from imports).
  const [showIndustryManager, setShowIndustryManager] = useState(false);

  // Fetch workspace-configured statuses. Auto-seeds on first read.
  const loadStatuses = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lead-statuses`);
      setLeadStatuses(data.rows || []);
    } catch { /* fall back to hard-coded on failure */ }
  };
  useEffect(() => { loadStatuses(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  // Order-preserving keys + label lookup for the rest of the page.
  const statusKeys = useMemo(
    () => leadStatuses.length ? leadStatuses.map(s => s.key) : FALLBACK_STATUSES,
    [leadStatuses]);
  const statusLabelMap = useMemo(() => {
    const m = {};
    leadStatuses.forEach(s => { m[s.key] = s.label; });
    return m;
  }, [leadStatuses]);
  const statusColorMap = useMemo(() => {
    const m = {};
    leadStatuses.forEach(s => { m[s.key] = s.color; });
    return m;
  }, [leadStatuses]);

  // Iter · Feb 2026 — Two-tier search state: `q` is the live input the
  // user is still typing in, `qCommitted` is what actually hits the
  // API. Explicit Search button (or Enter) commits, "×" clears.
  const [qCommitted, setQCommitted] = useState("");

  const currentFilters = useMemo(() => {
    const f = {};
    if (filterStatus) f.status = filterStatus;
    if (filterSource) f.source = filterSource;
    if (filterIndustry) f.industry = filterIndustry;
    if (filterSubIndustry) f.sub_industry = filterSubIndustry;
    if (filterTag) f.tag = filterTag;
    if (filterOwner) f.owner_user_id = filterOwner;
    if (filterOnDnd) f.on_dnd = "1";
    if (qCommitted) f.q = qCommitted;
    // R268nnnn · Feb 2026 — Multi-value filters serialised as CSV.
    if (filterCompanies?.length) f.companies = filterCompanies.join(",");
    if (filterDesignations?.length) f.designations = filterDesignations.join(",");
    if (filterCountries?.length) f.countries = filterCountries.join(",");
    if (filterCities?.length) f.cities = filterCities.join(",");
    return f;
  }, [filterStatus, filterSource, filterIndustry, filterSubIndustry, filterTag, filterOwner, filterOnDnd, qCommitted, filterCompanies, filterDesignations, filterCountries, filterCities]);

  // R138z · Feb 2026 — Perf: default fetch drops to 200 rows (was 1000).
  // "Filter-first" toggle blocks the initial auto-fetch entirely so users
  // on huge tenants can pick filters BEFORE the network hit. Both settings
  // persist per workspace.
  const [pageLimit, setPageLimit] = useState(() => {
    try { return Number(localStorage.getItem(`leads-page-limit:${activeWorkspaceId}`)) || 200; }
    catch { return 200; }
  });
  const [filterFirst, setFilterFirst] = useState(() => {
    // R268eeee · Feb 2026 — Default to "filter first" so brand-new
    // workspaces don't kick off an expensive full-table fetch on
    // page load. Users can turn it off from the toolbar toggle.
    try {
      const v = localStorage.getItem(`leads-filter-first:${activeWorkspaceId}`);
      if (v === "0") return false;
      return true;
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(`leads-page-limit:${activeWorkspaceId}`, String(pageLimit)); } catch { /* noop */ }
  }, [activeWorkspaceId, pageLimit]);
  useEffect(() => {
    try { localStorage.setItem(`leads-filter-first:${activeWorkspaceId}`, filterFirst ? "1" : "0"); } catch { /* noop */ }
  }, [activeWorkspaceId, filterFirst]);
  const hasAnyFilter = !!(filterStatus || filterSource || filterIndustry || filterSubIndustry || filterTag || filterOwner || filterOnDnd || q || filterCompanies?.length || filterDesignations?.length || filterCountries?.length || filterCities?.length);

  const load = async (opts = {}) => {
    if (!activeWorkspaceId) return;
    if (filterFirst && !hasAnyFilter && !opts.force) {
      // R138z · Skip the fetch until the user picks at least one filter
      // or clicks the "Load leads" button. R268eeee · Feb 2026 — Also
      // clear `busy` so the AdvancedTable shows the empty state
      // instead of an eternal spinner.
      setRows([]);
      setNextCursor(null);
      setBusy(false);
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams();
      Object.entries(currentFilters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set("lean", "1");
      params.set("include_signals", "0");
      // R267f · Feb 2026 — Use cursor pagination. Server returns
      // `{rows, next_cursor, has_more}` so we can lazy-load additional
      // pages without picking a huge upfront cap.
      params.set("paginate", "1");
      params.set("limit", String(opts.limit || pageLimit));
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/leads?${params.toString()}`);
      setRows(data?.rows || []);
      setNextCursor(data?.next_cursor || null);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load leads")); }
    finally { setBusy(false); }
  };

  // R267f · Feb 2026 — Load the next page and APPEND to `rows`. Called
  // by the "Load more" button + IntersectionObserver at the bottom
  // of the table. Debounced by `loadingMore` so a spammy sentinel
  // never fires two overlapping fetches.
  const loadMore = async () => {
    if (!activeWorkspaceId || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      Object.entries(currentFilters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set("lean", "1");
      params.set("include_signals", "0");
      params.set("paginate", "1");
      params.set("limit", String(pageLimit));
      params.set("cursor", nextCursor);
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/leads?${params.toString()}`);
      setRows((prev) => [...prev, ...(data?.rows || [])]);
      setNextCursor(data?.next_cursor || null);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load more")); }
    finally { setLoadingMore(false); }
  };
  // R268ssss · Feb 2026 — Filter changes no longer refetch. The user
  // stages any number of filter picks, then clicks the toolbar's
  // primary "Apply filters" button to fire one request that carries
  // every staged value at once. Cuts round-trips on Aquarius Global
  // where users used to trigger 4-5 sequential fetches while
  // narrowing down a segment. The useEffect below only reacts to
  // structural changes (workspace switch, filter-first toggle, page
  // limit) — everything else waits for Apply.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, filterFirst, pageLimit]);

  // Committed search + filter version — Apply increments this, useEffect
  // below refetches. Manual bump = zero extra state to track "dirty".
  const [filtersVersion, setFiltersVersion] = useState(0);
  // Feb 2026 · Apply is an explicit user intent to fetch — always force
  // through the filter-first short-circuit even when zero filters are
  // set, so clicking Apply on a clean toolbar returns ALL records
  // instead of the empty "Set filters, then click Apply" state.
  useEffect(() => { if (filtersVersion > 0) load({ force: true }); /* eslint-disable-next-line */ }, [filtersVersion, qCommitted]);
  const applyFilters = () => {
    setQCommitted(q.trim());
    setFiltersVersion((v) => v + 1);
  };

  // R267e · Feb 2026 — Lazy signal hydration. After `load()` paints the
  // rows (without any aggregations), fire ONE POST /leads/bulk-signals
  // for the visible slice to enrich WA + email reply counters. The
  // signals only affect optional pills, so it's safe to skip failures.
  useEffect(() => {
    if (!activeWorkspaceId || rows.length === 0) return;
    const ids = rows.map((r) => r.id).filter(Boolean).slice(0, 500);
    if (ids.length === 0) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(
          `/workspaces/${activeWorkspaceId}/leads/bulk-signals`,
          { lead_ids: ids });
        const signals = data?.signals || {};
        if (Object.keys(signals).length === 0) return;
        setRows((prev) => prev.map((r) => {
          const s = signals[r.id];
          if (!s) return r;
          return { ...r, ...s };
        }));
      } catch { /* silent — pills stay unhydrated */ }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId, rows.length && rows[0]?.id, rows.length]);

  // Feb 2026 · Deep-link: /app/leads?open=<lead_id> opens the LeadDialog
  // (used by the Hot Deals widget). Consume the query param once loaded.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && rows.length > 0) {
      const lead = rows.find((r) => r.id === openId);
      if (lead) {
        setEditing(lead);
        searchParams.delete("open");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [rows, searchParams, setSearchParams]);

  // Feb 2026 · fetch industry catalog (reused from clients), tags, and segments
  // R267e · Feb 2026 — Defer these secondary catalogs so they never
  // block first paint on huge tenants. They only feed dropdowns.
  // Oct 2026 · Switched to the workspace-scoped catalog so admins can
  // extend the industry master with workspace-only entries (and the
  // Leads form + filter dropdowns pick them up without a reload).
  const loadIndustries = () => {
    if (!activeWorkspaceId) return Promise.resolve();
    return api.get(`/workspaces/${activeWorkspaceId}/industries`)
      .then(({ data }) => setIndustriesCatalog(data))
      .catch(() => {
        // Fall back to the global catalog if the WS endpoint fails.
        return api.get(`/reference/industries`)
          .then(({ data }) => setIndustriesCatalog(data))
          .catch(() => {});
      });
  };
  useEffect(() => {
    if (!activeWorkspaceId) return;
    const t = setTimeout(() => { loadIndustries(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    const t = setTimeout(() => {
      api.get(`/workspaces/${activeWorkspaceId}/leads/tags`).then(({ data }) => setTagCatalog(data?.tags || [])).catch(() => setTagCatalog([]));
      api.get(`/workspaces/${activeWorkspaceId}/lead-segments`).then(({ data }) => setSegments(data?.rows || [])).catch(() => setSegments([]));
      // R268nnnn · Feb 2026 — Distinct Company / Designation / Country /
      // City values for the multi-select filters. One request, four
      // dropdowns. Deferred like other secondary catalogs so it never
      // blocks first paint on huge tenants.
      api.get(`/workspaces/${activeWorkspaceId}/leads/filter-options`).then(({ data }) => setFilterOptions({
        companies: data?.companies || [],
        designations: data?.designations || [],
        countries: data?.countries || [],
        cities: data?.cities || [],
      })).catch(() => setFilterOptions({ companies: [], designations: [], countries: [], cities: [] }));
    }, 400);
    return () => clearTimeout(t);
  }, [activeWorkspaceId]);

  // R133 · Track the currently applied segment so bulk actions can send
  // to the whole segment on the backend (avoids paging over all matches).
  const [currentSegmentId, setCurrentSegmentId] = useState(null);
  const applySegment = (s) => {
    const f = s?.filters || {};
    setFilterStatus(f.status || "");
    setFilterSource(f.source || "");
    setFilterIndustry(f.industry || "");
    setFilterSubIndustry(f.sub_industry || "");
    setFilterTag(f.tag || "");
    setQ(f.q || "");
    setCurrentSegmentId(s?.id || null);
    toast.success(`Applied segment "${s.name}"`);
  };
  const clearFilters = () => {
    setFilterStatus(""); setFilterSource(""); setFilterIndustry(""); setFilterSubIndustry(""); setFilterTag(""); setFilterOwner(""); setFilterOnDnd(false); setQ("");
    setFilterCompanies([]); setFilterDesignations([]); setFilterCountries([]); setFilterCities([]);
    setCurrentSegmentId(null);
  };
  const saveSegment = async (name) => {
    if (!name.trim()) return;
    setSavingSegment(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/lead-segments`, {
        name: name.trim(), filters: currentFilters,
      });
      setSegments((prev) => [data, ...prev]);
      toast.success(`Segment "${name}" saved`);
      setShowSaveSegment(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSavingSegment(false); }
  };
  const deleteSegment = async (s) => {
    if (!confirm(`Delete segment "${s.name}"?`)) return;
    try {
      await api.delete(`/lead-segments/${s.id}`);
      setSegments((prev) => prev.filter((x) => x.id !== s.id));
      toast.success("Segment deleted");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // Iter Feb 2026 R37 · Preset filter — "Warm now" — leads with any
  // engagement signal in the last 7 days AND a manual rating ≥ 3.
  // Client-side (no extra API call) so it composes with any active
  // segment / status / search query.
  const [warmNow, setWarmNow] = useState(false);

  const filtered = useMemo(() => {
    let list = rows;
    if (warmNow) {
      const cutoff = Date.now() - 7 * 24 * 3600 * 1000;
      list = list.filter((r) => {
        const rating = Number(r.manual_rating || 0);
        const touch = r.last_touch_at ? new Date(r.last_touch_at).getTime() : 0;
        return rating >= 3 && touch >= cutoff;
      });
    }
    if (!q) return list;
    const s = q.toLowerCase();
    return list.filter((r) => (r.name || "").toLowerCase().includes(s) || (r.company || "").toLowerCase().includes(s) || (r.email || "").toLowerCase().includes(s));
  }, [rows, q, warmNow]);

  const warmNowCount = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 3600 * 1000;
    return rows.filter((r) => {
      const rating = Number(r.manual_rating || 0);
      const touch = r.last_touch_at ? new Date(r.last_touch_at).getTime() : 0;
      return rating >= 3 && touch >= cutoff;
    }).length;
  }, [rows]);

  // Iter · Feb 2026 — Fixed status-tile counts. Old logic derived
  // counts from `rows`, which shrank the OTHER tiles to zero the
  // moment a status was clicked. Now we fetch counts from a dedicated
  // endpoint that ignores the status filter but respects all others
  // (source, industry, tag, owner, q) so tiles reflect the real total
  // per status inside the current non-status filter set. We also
  // surface `inFilter` = count of leads currently visible in the
  // grid, so the tiles can show BOTH "total per status" and
  // "N in filter" side-by-side.
  const [statusTileCounts, setStatusTileCounts] = useState({});
  useEffect(() => {
    if (!activeWorkspaceId) return;
    // R267e · Feb 2026 — Status funnel counts only render on the
    // Dashboard subtab; skip the aggregation entirely when the user
    // is on the leads list.
    if (view !== "dashboard") return;
    const params = new URLSearchParams();
    if (filterSource) params.set("source", filterSource);
    if (filterIndustry) params.set("industry", filterIndustry);
    if (filterSubIndustry) params.set("sub_industry", filterSubIndustry);
    if (filterTag) params.set("tag", filterTag);
    if (filterOwner) params.set("owner_user_id", filterOwner);
    if (qCommitted) params.set("q", qCommitted);
    api.get(`/workspaces/${activeWorkspaceId}/leads/status-counts?${params}`)
      .then(({ data }) => setStatusTileCounts(data || {}))
      .catch(() => setStatusTileCounts({}));
  }, [activeWorkspaceId, view, filterSource, filterIndustry, filterSubIndustry, filterTag, filterOwner, qCommitted]);

  const counts = useMemo(() => {
    // Prefer server-side unfiltered-by-status counts. Fall back to
    // row-derived counts when the endpoint hasn't answered yet.
    if (statusTileCounts && (statusTileCounts.all !== undefined)) {
      const out = { all: statusTileCounts.all || 0 };
      statusKeys.forEach((s) => { out[s] = statusTileCounts[s] || 0; });
      return out;
    }
    const out = { all: rows.length };
    statusKeys.forEach((s) => { out[s] = rows.filter((r) => r.status === s).length; });
    return out;
  }, [rows, statusKeys, statusTileCounts]);

  // Iter · Feb 2026 — Secondary "in current filter" counts (visible in
  // the grid right now). Rendered under the fixed total on each tile.
  const inFilterCounts = useMemo(() => {
    const out = { all: rows.length };
    statusKeys.forEach((s) => { out[s] = rows.filter((r) => r.status === s).length; });
    return out;
  }, [rows, statusKeys]);

  const convert = async (lead) => {
    if (!confirm(`Convert "${lead.name}" to a client?\nThis creates a new client (and a contact if email exists) in your workspace.`)) return;
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert`, { client_name: lead.company || lead.name });
      toast.success("Lead converted to client");
      await load();
      // navigate to client
      window.location.href = `/app/clients/${data.client_id}`;
    } catch (e) { toast.error(apiErrorMessage(e, "Convert failed")); }
  };

  const del = async (lead) => {
    if (!confirm(`Delete lead "${lead.name}"?`)) return;
    // R268oooo · Feb 2026 — Splice the row out of state instead of
    // re-fetching so the list keeps its scroll position and the user
    // doesn't stare at a blank page while the (potentially slow)
    // large-tenant list reloads.
    const idx = rows.findIndex((r) => r.id === lead.id);
    const snapshot = idx !== -1 ? rows.slice() : null;
    if (idx !== -1) {
      setRows((prev) => prev.filter((r) => r.id !== lead.id));
    }
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
    } catch (e) {
      // Roll back the local splice on failure.
      if (snapshot) setRows(snapshot);
      toast.error(apiErrorMessage(e, "Delete failed"));
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="CRM"
        title="Leads"
        description="Track prospects from first touch to deal close. Convert qualified leads into clients."
        testid="leads-header"
        actions={canManageLeads && (
          <div className="flex items-center gap-2 overflow-x-auto flex-nowrap max-w-full -mx-1 px-1 py-1 no-scrollbar" data-testid="leads-header-actions">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-none shrink-0 h-9 px-2.5" data-testid="leads-import-menu-btn">
                  <Upload className="w-4 h-4 mr-1.5" /> <span className="whitespace-nowrap">Import</span> <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/app/data-import?entity=leads")}
                                  data-testid="leads-import-new">
                  <Upload className="w-4 h-4 mr-2" /> Bulk import (CSV / XLSX)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/app/leads/import-history")}
                                  data-testid="leads-import-history">
                  <History className="w-4 h-4 mr-2" /> Import history
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("co_token");
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/workspaces/${activeWorkspaceId}/imports/leads/sample.xlsx`;
                      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                      if (!r.ok) throw new Error("Sample unavailable");
                      const blob = await r.blob();
                      const u = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = u; a.download = "acquilix-leads-sample.xlsx"; a.click();
                      URL.revokeObjectURL(u);
                    } catch (e) { toast.error("Sample not available"); }
                  }}
                  data-testid="leads-import-sample">
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Download sample XLSX
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" className="rounded-none shrink-0 h-9 px-2.5" onClick={() => navigate("/app/data-import?entity=leads")} data-testid="leads-import-btn">
              <Upload className="w-4 h-4 mr-1.5" /> <span className="whitespace-nowrap">Bulk import</span>
            </Button>
            <Button size="sm" className="rounded-none shrink-0 h-9 px-2.5" onClick={() => setEditing({})} data-testid="leads-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> <span className="whitespace-nowrap">New lead</span>
            </Button>
          </div>
        )}
      />
      <div className="px-3 sm:px-6 md:px-10">
        {/* R294 · Oct 2026 · Global customer search above the subtab
            bar. Searches across every lead field + comment/history
            text. Click a result to open Edit Lead. */}
        <div className="mb-3" data-testid="leads-global-search-wrap">
          <LeadsGlobalSearch
            wsId={activeWorkspaceId}
            onPickLead={(l) => setEditing(l)}
          />
        </div>
        {/* R267e · Feb 2026 — Dashboard vs Leads subtab strip.
            Landing on Leads by default keeps the fast path fast on
            huge tenants (Aquarius Global); Dashboard lazy-loads the
            WinLoss retro, status funnel counts and Hot Deals widget. */}
        <div className="flex items-center gap-1 border-b border-border mb-3 overflow-x-auto whitespace-nowrap no-scrollbar" data-testid="leads-subtabs">
          <button
            type="button"
            onClick={() => setView("leads")}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              view === "leads"
                ? "border-primary text-primary font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="leads-subtab-leads"
          >
            <Users className="w-3 h-3 inline mr-1" /> Leads list
          </button>
          <button
            type="button"
            onClick={() => setView("dashboard")}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              view === "dashboard"
                ? "border-primary text-primary font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="leads-subtab-dashboard"
          >
            <Sparkles className="w-3 h-3 inline mr-1" /> Dashboard
          </button>
          <button
            type="button"
            onClick={() => setView("pipeline")}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              view === "pipeline"
                ? "border-primary text-primary font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="leads-subtab-pipeline"
          >
            <LayoutGrid className="w-3 h-3 inline mr-1" /> Pipeline
          </button>
          <span className="ml-auto text-[10px] text-muted-foreground italic hidden sm:inline">
            {view === "dashboard"
              ? "Win-loss retro, status funnel, hot deals — the executive view."
              : view === "pipeline"
              ? "Tap the ⋮ menu to move a lead or reach out via call, WhatsApp or email."
              : "Fast list of every lead — search, filter, and act."}
          </span>
        </div>
        {view === "dashboard" && (
          <Suspense fallback={
            <div className="mb-4 border border-border bg-card p-4 text-xs text-muted-foreground animate-pulse" data-testid="leads-dashboard-loading">
              Loading dashboard…
            </div>
          }>
            <WinLossRetroPanel wsId={activeWorkspaceId} />
            <LeadsAnalyticsPanel wsId={activeWorkspaceId} />
            {/* R268pppp / R268rrrr · Feb 2026 — Owner-only rebuild-all
                button. One click ensures every hot-collection index
                (Leads, Tasks, Projects, Accounts, Activities, etc.).
                Toast surfaces per-collection created / active counts.
                Idempotent — safe to click repeatedly. */}
            {canManageLeads && (
              <div className="mb-3 flex items-center justify-end gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none h-8 text-[11px]"
                  data-testid="leads-fix-indexes-btn"
                  onClick={async (e) => {
                    const btn = e.currentTarget;
                    btn.setAttribute("disabled", "true");
                    try {
                      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/admin/rebuild-all-indexes`);
                      const t = data?.totals || {};
                      const rows = (data?.collections || [])
                        .filter((c) => c.created > 0 || c.errors > 0)
                        .map((c) => `${c.collection}: ${c.created}`)
                        .slice(0, 6)
                        .join(" · ");
                      toast.success(
                        `Rebuilt indexes across ${t.collection_count} collections — ${t.created ?? 0} ensured` +
                        (t.errors ? ` · ${t.errors} errors` : "") +
                        ` (${t.elapsed_ms ?? 0}ms)${rows ? " · " + rows : ""}`,
                        { duration: 8000 },
                      );
                    } catch (err) {
                      toast.error(apiErrorMessage(err, "Rebuild failed"));
                    } finally {
                      btn.removeAttribute("disabled");
                    }
                  }}
                >
                  Rebuild all indexes
                </Button>
                <span className="text-[10px] text-muted-foreground">
                  Ensures every index for Leads, Tasks, Projects, Accounts, Clients, Brands, Activities & Notifications — safe to click repeatedly after a big data import.
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-3 items-stretch">
              <StatTile label="All" count={counts.all} subCount={filterStatus ? inFilterCounts.all : null} active={!filterStatus} onClick={() => { setFilterStatus(""); setFiltersVersion((v) => v + 1); }} testid="leads-stat-all" />
              {statusKeys.map((s) => (
                <StatTile key={s}
                          label={statusLabelMap[s] || s}
                          color={statusColorMap[s]}
                          count={counts[s] || 0}
                          subCount={filterStatus && filterStatus !== s ? null : inFilterCounts[s]}
                          active={filterStatus === s}
                          onClick={() => { setFilterStatus(filterStatus === s ? "" : s); setFiltersVersion((v) => v + 1); }}
                          testid={`leads-stat-${s}`} />
              ))}
              {/* R68 · Manage workspace lead statuses */}
              {canManageLeads && (
                <button
                  type="button"
                  onClick={() => setShowStatusManager(true)}
                  className="border border-dashed border-border hover:bg-muted/40 flex flex-col items-center justify-center text-[10px] uppercase tracking-widest text-muted-foreground gap-1 min-h-[64px]"
                  title="Manage workspace lead statuses"
                  data-testid="leads-manage-statuses"
                >
                  <Settings2 className="w-3.5 h-3.5" />
                  Manage statuses
                </button>
              )}
            </div>
          </Suspense>
        )}

        {/* R293 · Oct 2026 · List mode body — segments, mobile toolbar,
            filter bar, view toggle, table/cards and infinite scroll are
            all list-only. Dashboard subtab now renders ONLY the
            analytics panels above. */}
        {/* Feb 2026 · Saved segments strip — collapsed to a dropdown
            (keeps "Warm now" as a quick toggle). Scales to any number
            of saved segments without hogging screen width on phones. */}
        <div className={`${view === "leads" || view === "pipeline" ? "" : "hidden"} mb-3 flex items-center gap-2 flex-wrap`} data-testid="leads-segments-strip">
          <span className="label-eyebrow inline-flex items-center gap-1 shrink-0">
            <Bookmark className="w-3 h-3" /> Segments
          </span>
          {/* Iter Feb 2026 R37 · Curated "Warm now" preset — leads with a
               rating ≥ 3★ AND any touch (WA reply, email open/click,
               update) in the last 7 days. Shipped as a first-class chip
               so the sales team can jump straight to who's hot without
               configuring filters each morning. */}
          <button
            type="button"
            onClick={() => setWarmNow((v) => !v)}
            className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] transition-colors shrink-0 ${
              warmNow
                ? "bg-amber-500 text-black border-amber-500"
                : "bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"
            }`}
            data-testid="lead-segment-warm-now"
            title="Leads with any engagement in the last 7 days AND rated ≥ 3★"
          >
            <Flame className="w-3 h-3" />
            Warm now
            <span className={`text-[10px] px-1 border ${warmNow ? "border-black/30 bg-black/10" : "border-amber-400 bg-amber-100"}`}>
              {warmNowCount}
            </span>
          </button>
          {/* Dropdown — picks any saved segment. "— None —" clears the
              currently-applied segment. Only renders when the user has
              at least one saved segment. */}
          {segments.length === 0 ? (
            <span className="text-[10px] text-muted-foreground italic">No saved segments yet — filter below and click &quot;Save as segment&quot;.</span>
          ) : (
            <Select
              value={currentSegmentId || "__none__"}
              onValueChange={(v) => {
                if (v === "__none__") { clearFilters(); setFiltersVersion((x) => x + 1); return; }
                const s = segments.find((x) => x.id === v);
                if (s) applySegment(s);
              }}
            >
              <SelectTrigger className="h-8 min-w-[180px] max-w-[260px] rounded-none text-xs" data-testid="leads-segments-select">
                <SelectValue placeholder="Pick a saved segment…" />
              </SelectTrigger>
              <SelectContent className="max-h-[320px]">
                <SelectItem value="__none__">— None —</SelectItem>
                {segments.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`lead-segment-${s.id}`}>
                    {s.name}
                    {s.lead_count != null ? (
                      <span className="ml-1 text-[10px] text-muted-foreground">· {s.lead_count}</span>
                    ) : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Inline actions for the currently-applied segment: refresh
              counter, send WhatsApp/Email, delete. Sit next to the
              dropdown so power users still get one-click access. */}
          {currentSegmentId && (() => {
            const s = segments.find((x) => x.id === currentSegmentId);
            if (!s) return null;
            return (
              <span className="inline-flex items-center border border-primary bg-primary/10 shrink-0" data-testid={`lead-segment-current-${s.id}`}>
                <SegmentHealthChip seg={s} wsId={activeWorkspaceId}
                                    onRefresh={(updated) => {
                                      setSegments((prev) => prev.map((x) => x.id === s.id ? { ...x, ...updated } : x));
                                    }} />
                <button
                  className="px-1.5 py-0.5 text-[11px] text-emerald-700 hover:bg-emerald-100 border-l border-border"
                  onClick={() => { setSelectedIds(new Set()); setShowBulkWa(true); }}
                  title="Send WhatsApp to this segment"
                  data-testid={`lead-segment-wa-${s.id}`}>
                  <MessageCircle className="w-3 h-3" />
                </button>
                <button
                  className="px-1.5 py-0.5 text-[11px] text-blue-700 hover:bg-blue-100 border-l border-border"
                  onClick={() => { setSelectedIds(new Set()); setShowBulkEmail(true); }}
                  title="Send email to this segment"
                  data-testid={`lead-segment-email-${s.id}`}>
                  <Mail className="w-3 h-3" />
                </button>
                {canManageLeads && (
                  <button className="px-1.5 py-0.5 text-[11px] text-muted-foreground hover:text-rose-600 border-l border-border" onClick={() => deleteSegment(s)} title="Delete segment"
                           data-testid={`lead-segment-delete-${s.id}`}>
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })()}
        </div>

        {/* R293 · Oct 2026 · Mobile toolbar — compact row with just
            Search, Filters button + active-count, and the Table/Cards
            toggle. The full filter bar below is `hidden md:flex` so
            phones don't drown in dropdowns. */}
        <div className={`${view === "leads" || view === "pipeline" ? "flex" : "hidden"} items-center gap-2 md:hidden mb-2`} data-testid="leads-mobile-toolbar">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyFilters(); } }}
              placeholder="Search leads…"
              className="h-9 w-full text-xs pl-7 pr-7 rounded-none"
              data-testid="leads-q-mobile"
            />
            {(q || qCommitted) && (
              <button
                type="button"
                onClick={() => { setQ(""); setQCommitted(""); }}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
                data-testid="leads-q-clear-mobile"
              ><X className="w-3 h-3" /></button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setFilterSheetOpen(true)}
            className="h-9 px-3 rounded-none border border-border bg-background text-xs inline-flex items-center gap-1.5 whitespace-nowrap relative"
            data-testid="leads-filters-sheet-open"
          >
            <FilterIcon className="w-3.5 h-3.5" />
            Filters
            {(filterStatus || filterSource || filterIndustry || filterSubIndustry || filterTag || filterOwner || (filterCompanies?.length || 0) + (filterDesignations?.length || 0) + (filterCountries?.length || 0) + (filterCities?.length || 0) > 0) && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-primary text-primary-foreground text-[9px] font-bold rounded-full inline-flex items-center justify-center" data-testid="leads-filters-badge">
                {[filterStatus, filterSource, filterIndustry, filterSubIndustry, filterTag, filterOwner].filter(Boolean).length +
                  (filterCompanies?.length || 0) + (filterDesignations?.length || 0) + (filterCountries?.length || 0) + (filterCities?.length || 0)}
              </span>
            )}
          </button>
        </div>

        {/* R293 · Oct 2026 · Leads list view mode toggle (Table / Cards /
            Compact). Visible on every viewport. Persists per user. */}
        <div className={`${view === "leads" ? "flex" : "hidden"} items-center justify-between gap-2 mb-2`} data-testid="leads-view-toggle-row">
          <div className="inline-flex border border-border bg-background rounded-none" role="group" aria-label="Leads view mode">
            <button
              type="button"
              onClick={() => setLeadsListMode("table")}
              className={`px-2 sm:px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest border-r border-border transition-colors ${leadsListMode === "table" ? "bg-foreground text-background" : "bg-background text-muted-foreground hover:text-foreground"}`}
              data-testid="leads-view-table"
              aria-pressed={leadsListMode === "table"}
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => setLeadsListMode("cards")}
              className={`px-2 sm:px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest border-r border-border transition-colors ${leadsListMode === "cards" ? "bg-foreground text-background" : "bg-background text-muted-foreground hover:text-foreground"}`}
              data-testid="leads-view-cards"
              aria-pressed={leadsListMode === "cards"}
            >
              Cards
            </button>
            <button
              type="button"
              onClick={() => setLeadsListMode("compact")}
              className={`px-2 sm:px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest transition-colors ${leadsListMode === "compact" ? "bg-foreground text-background" : "bg-background text-muted-foreground hover:text-foreground"}`}
              data-testid="leads-view-compact"
              aria-pressed={leadsListMode === "compact"}
            >
              Compact
            </button>
          </div>
          <div className="text-[11px] text-muted-foreground whitespace-nowrap">
            {filtered.length} {filtered.length === 1 ? "lead" : "leads"}
          </div>
        </div>

        {/* Feb 2026 · Filters panel (segmentation) */}
        <div className={`${view === "leads" || view === "pipeline" ? "hidden md:flex" : "hidden"} border border-border bg-muted/40 p-3 items-center gap-2 flex-wrap`} data-testid="leads-filters">
          <Search className="w-3.5 h-3.5" />
          {/* Iter · Feb 2026 — Explicit Search + inline Clear. The old
              per-keystroke fetch made typing feel laggy on big
              workspaces; now the API only fires when the user hits
              Enter or clicks Search, and Clear pops the input + wipes
              qCommitted so the full data set returns.
              R268ssss · Feb 2026 — Killed the separate Search button.
              A 400ms debounce auto-commits `q` so the toolbar mirrors
              standard CRM UX (Hubspot / Pipedrive). Clear-button + Enter
              still work. */}
          <div className="relative inline-flex items-center w-full sm:w-72">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyFilters(); } }}
              placeholder="Search name, company, email, phone… (Enter to apply)"
              className="h-8 w-full text-xs pr-7"
              data-testid="leads-q"
            />
            {(q || qCommitted) && (
              <button
                type="button"
                onClick={() => { setQ(""); setQCommitted(""); }}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
                title="Clear search"
                data-testid="leads-q-clear"
              ><X className="w-3 h-3" /></button>
            )}
          </div>
          {/* R268ssss · Feb 2026 — Explicit Status filter dropdown in
              the toolbar so PMs can slice by status without leaving the
              list to click a KPI tile. Bound to the same `filterStatus`
              state, so KPI tiles + dropdown stay in sync. */}
          <Select value={filterStatus || "__all__"} onValueChange={(v) => setFilterStatus(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-full sm:w-[130px] rounded-none text-xs" data-testid="leads-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterSource || "__all__"} onValueChange={(v) => setFilterSource(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-full sm:w-[130px] rounded-none text-xs" data-testid="leads-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sources</SelectItem>
              {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterIndustry || "__all__"} onValueChange={(v) => {
            if (v === "__manage__") { setShowIndustryManager(true); return; }
            setFilterIndustry(v === "__all__" ? "" : v); setFilterSubIndustry("");
          }}>
            <SelectTrigger className="h-8 w-full sm:w-[160px] rounded-none text-xs" data-testid="leads-filter-industry"><SelectValue placeholder="Industry" /></SelectTrigger>
            <SelectContent className="max-h-[280px]">
              <SelectItem value="__all__">All industries</SelectItem>
              {(industriesCatalog.industries || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              {/* Oct 2026 · Surface the Industry Manager from inside
                  the filter dropdown too — users looking for 'where
                  do I add a new industry?' naturally open this list
                  first. The dedicated button to the right stays for
                  one-click access with a tooltip. */}
              {canManageLeads && (
                <>
                  <div className="my-1 border-t border-border" aria-hidden="true" />
                  <SelectItem value="__manage__" className="text-primary">
                    + Manage industries & sub-industries…
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
          {canManageLeads && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-none h-8 px-2 gap-1 text-xs whitespace-nowrap"
              title="Add / manage workspace industries and sub-industries, including unmatched values from imports"
              onClick={() => setShowIndustryManager(true)}
              data-testid="leads-manage-industries-btn"
            >
              <Settings2 className="w-3.5 h-3.5" />
              Manage
            </Button>
          )}
          <Select value={filterSubIndustry || "__all__"}
                   onValueChange={(v) => setFilterSubIndustry(v === "__all__" ? "" : v)}
                   disabled={!filterIndustry}>
            <SelectTrigger className="h-8 w-full sm:w-[160px] rounded-none text-xs" data-testid="leads-filter-subindustry"><SelectValue placeholder="Sub-industry" /></SelectTrigger>
            <SelectContent className="max-h-[280px]">
              <SelectItem value="__all__">All</SelectItem>
              {(industriesCatalog.sub_industries?.[filterIndustry] || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterTag || "__all__"} onValueChange={(v) => setFilterTag(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-full sm:w-[130px] rounded-none text-xs" data-testid="leads-filter-tag"><SelectValue placeholder="Tag" /></SelectTrigger>
            <SelectContent className="max-h-[280px]">
              <SelectItem value="__all__">All tags</SelectItem>
              {tagCatalog.map((t) => <SelectItem key={t.name} value={t.name}>{t.name} ({t.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterOwner || "__all__"} onValueChange={(v) => setFilterOwner(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-full sm:w-[150px] rounded-none text-xs" data-testid="leads-filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent className="max-h-[280px]">
              <SelectItem value="__all__">All owners</SelectItem>
              <SelectItem value="me">Assigned to me</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {wsMembers.map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>
                  {m.name || m.email || m.user_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Feb 2026 — DND-only quick chip. Toggles `on_dnd=1` on
              the leads list so SDRs can pull the no-call segment in
              one tap for cleanup / review. */}
          <button
            type="button"
            onClick={() => setFilterOnDnd((v) => !v)}
            className={`h-8 px-2 text-[11px] border font-semibold inline-flex items-center gap-1 ${filterOnDnd ? "bg-rose-50 border-rose-400 text-rose-700" : "bg-background border-border text-muted-foreground hover:bg-muted/40"}`}
            data-testid="leads-filter-dnd-chip"
            title="Only leads whose phone is on the workspace DND blocklist"
          >
            <span className="w-1.5 h-1.5 bg-rose-500 rounded-full inline-block" />
            Only DND
          </button>
          {/* R268nnnn · Feb 2026 — Sorted, searchable, multi-select
              filters populated from the workspace's actual leads
              (via /leads/filter-options). Empty ⇒ dropdown hidden so
              tiny workspaces don't see a "0 companies" chip. */}
          {filterOptions.companies.length > 0 && (
            <MultiSelect
              options={filterOptions.companies.map((c) => ({ value: c.value, label: `${c.value} (${c.count})` }))}
              value={filterCompanies}
              onChange={setFilterCompanies}
              placeholder="Company"
              allLabel="All companies"
              width="w-full sm:w-[160px]"
              testid="leads-filter-company"
            />
          )}
          {filterOptions.designations.length > 0 && (
            <MultiSelect
              options={filterOptions.designations.map((c) => ({ value: c.value, label: `${c.value} (${c.count})` }))}
              value={filterDesignations}
              onChange={setFilterDesignations}
              placeholder="Designation"
              allLabel="All designations"
              width="w-full sm:w-[160px]"
              testid="leads-filter-designation"
            />
          )}
          {filterOptions.countries.length > 0 && (
            <MultiSelect
              options={filterOptions.countries.map((c) => ({ value: c.value, label: `${c.value} (${c.count})` }))}
              value={filterCountries}
              onChange={setFilterCountries}
              placeholder="Country"
              allLabel="All countries"
              width="w-full sm:w-[140px]"
              testid="leads-filter-country"
            />
          )}
          {filterOptions.cities.length > 0 && (
            <MultiSelect
              options={filterOptions.cities.map((c) => ({ value: c.value, label: `${c.value} (${c.count})` }))}
              value={filterCities}
              onChange={setFilterCities}
              placeholder="City"
              allLabel="All cities"
              width="w-full sm:w-[140px]"
              testid="leads-filter-city"
            />
          )}
          {/* R268ssss · Feb 2026 — Primary "Apply filters" CTA fires
              one aggregated request that carries every staged filter
              + search. Previously every dropdown pick triggered its
              own fetch (4-5 round-trips per narrowing session).
              Killed the separate "Load leads" CTA — Apply covers the
              filter-first flow too. */}
          <Button
            size="sm"
            className="rounded-none h-8 text-[11px] gap-1"
            onClick={applyFilters}
            data-testid="leads-apply-filters"
          >
            <Search className="w-3 h-3" />
            Apply filters
            {busy && <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin ml-1" aria-hidden />}
          </Button>
          {/* Feb 2026 · Applied-filters chip. Makes it obvious when a
              user has deliberately loaded the full list (0 filters ·
              N records) vs. is still narrowing down. */}
          {filtersVersion > 0 && !busy && (
            <span
              className="inline-flex items-center h-8 px-2 border border-border rounded-none text-[10.5px] font-mono tracking-tight text-muted-foreground bg-muted/30"
              data-testid="leads-filter-chip"
              title="Filters applied on the last request · records currently loaded"
            >
              {(() => {
                const singleFilters = [filterStatus, filterSource, filterIndustry, filterSubIndustry, filterTag, filterOwner, qCommitted].filter(Boolean).length;
                const multiFilters = [filterCompanies, filterDesignations, filterCountries, filterCities].filter((a) => a && a.length > 0).length;
                const total = singleFilters + multiFilters;
                return `${total} filter${total === 1 ? "" : "s"} · ${rows.length.toLocaleString()} record${rows.length === 1 ? "" : "s"} loaded`;
              })()}
            </span>
          )}
          {(filterStatus || filterSource || filterIndustry || filterSubIndustry || filterTag || filterOwner || q || filterCompanies.length || filterDesignations.length || filterCountries.length || filterCities.length) ? (
            <Button size="sm" variant="ghost" className="rounded-none h-8 text-[11px]" onClick={() => { clearFilters(); setFiltersVersion((v) => v + 1); }} data-testid="leads-filters-clear">
              Clear
            </Button>
          ) : null}
          <Button size="sm" variant="outline" className="rounded-none h-8 text-[11px]" onClick={() => setShowSaveSegment(true)} data-testid="leads-save-segment">
            <Save className="w-3 h-3 mr-1" /> Save as segment
          </Button>
          {/* R138z · Perf controls */}
          <div className="ml-auto flex items-center gap-2 text-[11px]">
            <label className="flex items-center gap-1 cursor-pointer" title="Skip auto-fetch until at least one filter is applied">
              <input type="checkbox" checked={filterFirst}
                         onChange={(e) => setFilterFirst(e.target.checked)}
                         className="h-3 w-3 accent-primary"
                         data-testid="leads-filter-first" />
              <span>Filter first</span>
            </label>
            <Select value={String(pageLimit)} onValueChange={(v) => setPageLimit(Number(v))}>
              <SelectTrigger className="h-7 w-20 rounded-none text-[11px]" data-testid="leads-page-limit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="200">200</SelectItem>
                <SelectItem value="500">500</SelectItem>
                <SelectItem value="1000">1000</SelectItem>
                <SelectItem value="2000">2000</SelectItem>
              </SelectContent>
            </Select>
            <span className="inline-flex items-center gap-1" data-testid="leads-count-chip">
              {busy && <span className="inline-block w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-hidden />}
              <span>{filtered.length} of {rows.length}</span>
            </span>
          </div>
        </div>
      </div>
      <div className="px-3 sm:px-6 md:px-10 py-4 sm:py-6">
        {/* Feb 2026 · Hot Deals — top of Leads page for at-a-glance urgent callbacks
            R267e · Feb 2026 — Moved under Dashboard tab so the Leads
            list paints without waiting on the hot-deals aggregation. */}
        {view === "dashboard" && (
          <div className="mb-4">
            <HotDealsWidget testid="leads-hot-deals" />
          </div>
        )}
        {/* Feb 2026 · Pipeline (kanban-by-status) subtab. Mobile-first
            — horizontal scroll between columns, swipe cards right/left
            to advance a deal, long-press for a status picker. Falls
            back to the dynamic workspace statusKeys so admin-added
            custom statuses show up as their own column. */}
        {view === "pipeline" && (
          <LeadsPipelineView
            rows={filtered}
            loading={busy && rows.length === 0}
            statusKeys={statusKeys}
            statusLabelMap={statusLabelMap}
            statusColorMap={statusColorMap}
            onRowClick={(l) => setEditing(l)}
            onStatusChanged={(leadId, newStatus, serverRow) => {
              setRows((prev) => prev.map((r) => r.id === leadId
                ? { ...r, status: newStatus, ...(serverRow || {}) }
                : r));
            }}
            emptyTitle={
              filterFirst && !hasAnyFilter && rows.length === 0
                ? "Set filters, then click Apply"
                : rows.length === 0
                  ? "No leads yet"
                  : "No leads match your filters"
            }
            emptyDescription={
              filterFirst && !hasAnyFilter && rows.length === 0
                ? "Pick any filter above (status, owner, search…) and click Apply filters."
                : "Capture prospects from forms, referrals, or outbound campaigns."
            }
          />
        )}
        {view === "leads" && (() => {
          const columns = [
            // Oct 2026 · Separate Name · Title · Company · Email · Phone
            // columns so power users can slice, sort, group and export
            // each as its own field. The old composite "Name · company"
            // cell crammed four signals into one row; the compact
            // split keeps rows short and the Excel export clean.
            { key: "name", label: "Name", width: 180, sortable: true, filterType: "text", frozen: true,
              exportValue: (l) => l.name || "",
              render: (l) => (
                <div className="flex items-center gap-2 min-w-0 w-full">
                  <span className="font-semibold truncate flex-1 min-w-0">{l.name}</span>
                  {/* Feb 2026 · Red DND badge when the lead's phone is on
                      the workspace blocklist. Enriched by the leads list
                      endpoint in one bulk Mongo lookup. */}
                  {l.on_dnd && (
                    <span
                      className="chip text-[9px] bg-rose-50 border-rose-400 text-rose-700 font-semibold px-1 py-0 flex-shrink-0"
                      title="This number is on your workspace DND blocklist"
                      data-testid={`lead-dnd-badge-${l.id}`}
                    >
                      DND
                    </span>
                  )}
                  {/* Oct 2026 · Quick-action icons right-aligned in the
                      Name column so the name text itself never gets
                      pushed off-screen on narrow viewports. */}
                  <span className="flex-shrink-0 ml-auto">
                    <QuickContactActions
                      entityType="lead" entityId={l.id} entityName={l.name || l.company}
                      phone={l.phone || l.mobile || l.office_phone} email={l.email}
                    />
                  </span>
                </div>
              ),
            },
            { key: "title", label: "Title", width: 150, sortable: true, filterType: "text",
              accessor: (l) => l.title || "",
              exportValue: (l) => l.title || "",
              render: (l) => <span className="text-xs truncate">{l.title || "—"}</span>,
            },
            { key: "company", label: "Company", width: 180, sortable: true, groupable: true, filterType: "text",
              accessor: (l) => l.company || "",
              exportValue: (l) => l.company || "",
              render: (l) => (
                <div className="text-xs truncate">
                  <div className="truncate">{l.company || "—"}</div>
                  {l.city ? <div className="text-[10px] text-muted-foreground truncate">{l.city}</div> : null}
                </div>
              ),
            },
            { key: "email", label: "Email", width: 200, sortable: true, filterType: "text",
              accessor: (l) => l.email || l.secondary_email || "",
              exportValue: (l) => l.email || l.secondary_email || "",
              render: (l) => <span className="text-xs truncate">{l.email || l.secondary_email || "—"}</span>,
            },
            { key: "phone", label: "Phone", width: 140, sortable: true, filterType: "text",
              accessor: (l) => l.mobile || l.phone || l.office_phone || "",
              exportValue: (l) => l.mobile || l.phone || l.office_phone || "",
              render: (l) => <span className="text-xs truncate tabular-nums">{l.mobile || l.phone || l.office_phone || "—"}</span>,
            },
            { key: "source", label: "Source", width: 110, sortable: true, groupable: true, filterType: "select",
              filterOptions: SOURCES.map((v) => ({ value: v, label: v })),
              render: (l) => <span className="text-xs">{l.source || "—"}</span>,
            },
            { key: "status", label: "Status", width: 110, sortable: true, groupable: true, filterType: "select",
              filterOptions: statusKeys.map((v) => ({ value: v, label: statusLabelMap[v] || v })),
              render: (l) => (
                <span
                  className={`chip text-[10px] ${STATUS_COLOR[l.status] || ""}`}
                  style={statusColorMap[l.status] ? { background: statusColorMap[l.status] + "22", borderLeft: `3px solid ${statusColorMap[l.status]}`, paddingLeft: 6 } : undefined}
                >
                  {statusLabelMap[l.status] || l.status}
                </span>
              ),
            },
            { key: "temperature_effective", label: "Temp", width: 90, sortable: true, groupable: true,
              accessor: (l) => l.temperature_effective || l.temperature_ai || "cold",
              render: (l) => (
                <TemperaturePill
                  value={l.temperature_manual || l.temperature_ai}
                  onChange={(v) => {
                    // R268pppp · Feb 2026 — Eagerly optimistic (see
                    // Owner cell above for pattern). Splice first,
                    // fire PATCH in background, roll back on error.
                    const prevManual = l.temperature_manual || null;
                    const prevEff = l.temperature_effective;
                    setRows((prev) => prev.map((r) => r.id === l.id
                      ? { ...r, temperature_manual: v || null, temperature_effective: v || l.temperature_ai }
                      : r));
                    api.patch(`/leads/${l.id}`, { temperature_manual: v || null }).catch((err) => {
                      setRows((prev) => prev.map((r) => r.id === l.id
                        ? { ...r, temperature_manual: prevManual, temperature_effective: prevEff }
                        : r));
                      toast.error(apiErrorMessage(err, "Update failed"));
                    });
                  }}
                  testid={`lead-temp-${l.id}`}
                />
              ),
            },
            { key: "next_followup_at", label: "Next follow-up", width: 140, sortable: true, filterType: "date-range",
              accessor: (l) => l.next_followup_at || "",
              render: (l) => {
                const d = l.next_followup_at;
                if (!d) return <span className="text-muted-foreground text-xs">—</span>;
                const overdue = new Date(d) < new Date();
                return (
                  <span className={`text-xs tabular-nums ${overdue ? "text-rose-700 font-semibold" : "text-muted-foreground"}`} title={overdue ? "Overdue follow-up" : d} data-testid={`lead-next-followup-${l.id}`}>
                    {new Date(d).toLocaleDateString()}
                  </span>
                );
              },
            },
            { key: "industry", label: "Industry", width: 140, sortable: true, groupable: true, filterType: "text",
              render: (l) => <span className="text-xs">{l.industry || "—"}</span>,
            },
            { key: "sub_industry", label: "Sub-industry", width: 150, sortable: true, filterType: "text",
              render: (l) => <span className="text-xs text-muted-foreground">{l.sub_industry || "—"}</span>,
            },
            { key: "tags", label: "Tags", width: 160,
              render: (l) => (
                <div className="flex flex-wrap gap-0.5">
                  {(l.tags || []).slice(0, 4).map((t) => (
                    <span key={t} className="chip text-[9px]">{t}</span>
                  ))}
                  {(l.tags || []).length === 0 && <span className="text-muted-foreground text-xs">—</span>}
                </div>
              ),
            },
            // Iter Feb 2026 R36 · Engagement signals — who's warm & responsive?
            // • WA badge shows total inbound replies + last-reply relative time.
            // • Email badge shows opens/clicks + last-opened relative time.
            // • Rating column lets sales manually score a lead 1-5 (inline).
            // • Last touch is the most recent of any signal (auto-computed
            //   backend-side across WA reply / email open+click / update).
            { key: "wa_replies_count", label: "WA replies", width: 130, sortable: true,
              accessor: (l) => Number(l.wa_replies_count || 0),
              render: (l) => {
                const n = Number(l.wa_replies_count || 0);
                if (!n) return <span className="text-muted-foreground text-xs">—</span>;
                return (
                  <div className="flex items-center gap-1.5" title={`${n} inbound WhatsApp message${n === 1 ? "" : "s"} · last ${l.wa_last_reply_at || ""}`}
                        data-testid={`lead-wa-badge-${l.id}`}>
                    <span className="chip text-[10px] bg-emerald-50 border-emerald-300 text-emerald-800">
                      💬 {n}
                    </span>
                    {l.wa_last_reply_at && (
                      <span className="text-[10px] text-muted-foreground">{fmtRelative(l.wa_last_reply_at)}</span>
                    )}
                  </div>
                );
              },
            },
            { key: "email_engagement", label: "Email", width: 130, sortable: true,
              accessor: (l) => Number(l.email_opens_count || 0) * 10 + Number(l.email_clicks_count || 0),
              render: (l) => {
                const opens = Number(l.email_opens_count || 0);
                const clicks = Number(l.email_clicks_count || 0);
                if (!opens && !clicks) return <span className="text-muted-foreground text-xs">—</span>;
                const at = l.email_last_clicked_at || l.email_last_opened_at;
                return (
                  <div className="flex items-center gap-1.5"
                        title={`${opens} open${opens === 1 ? "" : "s"} · ${clicks} click${clicks === 1 ? "" : "s"}`}
                        data-testid={`lead-email-badge-${l.id}`}>
                    <span className="chip text-[10px] bg-sky-50 border-sky-300 text-sky-800">
                      👁 {opens}{clicks ? ` · 🖱 ${clicks}` : ""}
                    </span>
                    {at && <span className="text-[10px] text-muted-foreground">{fmtRelative(at)}</span>}
                  </div>
                );
              },
            },
            // R138ca · Feb 2026 — Email replies pill. Fed by the IMAP
            // scanner that stamps `replied_at` on entity_email_activities
            // when a customer replies to a bulk / composer send. Green
            // pill so reps can spot hot responses without opening the
            // History drawer.
            { key: "email_replies_count", label: "Replies", width: 110, sortable: true,
              accessor: (l) => Number(l.email_replies_count || 0),
              render: (l) => {
                const n = Number(l.email_replies_count || 0);
                if (!n) return <span className="text-muted-foreground text-xs">—</span>;
                const at = l.email_last_replied_at;
                return (
                  <div className="flex items-center gap-1.5"
                        title={`${n} reply${n === 1 ? "" : "s"}${at ? ` · latest ${at}` : ""}`}
                        data-testid={`lead-email-replies-badge-${l.id}`}>
                    <span className="chip text-[10px] bg-emerald-50 border-emerald-400 text-emerald-800 font-semibold">
                      ✉ Replied · {n}
                    </span>
                    {at && <span className="text-[10px] text-muted-foreground">{fmtRelative(at)}</span>}
                  </div>
                );
              },
            },
            { key: "manual_rating", label: "Rating", width: 130, sortable: true, align: "center",
              accessor: (l) => Number(l.manual_rating || 0),
              render: (l) => (
                <LeadRatingCell lead={l} onChanged={(newRating) => {
                  setRows((prev) => prev.map((r) => r.id === l.id ? { ...r, manual_rating: newRating } : r));
                }} />
              ),
            },
            { key: "last_touch_at", label: "Last touch", width: 120, sortable: true, filterType: "date-range",
              accessor: (l) => l.last_touch_at || l.updated_at || l.created_at,
              render: (l) => (
                <span className="text-xs text-muted-foreground"
                        title={l.last_touch_at || l.updated_at || l.created_at || ""}>
                  {fmtRelative(l.last_touch_at || l.updated_at || l.created_at)}
                </span>
              ),
            },
            // Iter · Feb 2026 — Latest follow-up note preview. Fed by the
            // denormalised `latest_note_*` fields on the lead doc (set on
            // POST /leads/{lid}/followups). Users asked to see who added
            // what last note on the lead at-a-glance in the list view.
            { key: "latest_note_body", label: "Latest note", width: 260, sortable: true, filterType: "text",
              accessor: (l) => l.latest_note_body || "",
              render: (l) => {
                if (!l.latest_note_body) return <span className="text-muted-foreground text-xs">—</span>;
                return (
                  <div className="min-w-0 text-xs" title={l.latest_note_body} data-testid={`lead-latest-note-${l.id}`}>
                    <div className="truncate">{l.latest_note_body}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {l.latest_note_by_name || "—"}
                      {l.latest_note_at ? ` · ${fmtRelative(l.latest_note_at)}` : ""}
                      {l.latest_note_channel && l.latest_note_channel !== "manual" ? ` · ${l.latest_note_channel}` : ""}
                    </div>
                  </div>
                );
              },
            },
            { key: "estimated_value", label: "Est. value", width: 140, sortable: true, align: "right", type: "currency", sum: true, filterType: "number-range",
              accessor: (l) => Number(l.estimated_value || 0),
              render: (l) => <span className="font-mono text-xs">{l.estimated_value ? `${l.currency || "INR"} ${Number(l.estimated_value).toLocaleString()}` : "—"}</span>,
            },
            { key: "updated_at", label: "Updated", width: 130, sortable: true, filterType: "date-range",
              accessor: (l) => l.updated_at || l.created_at,
              render: (l) => <span className="text-xs text-muted-foreground">{fmtRelative(l.updated_at || l.created_at)}</span>,
            },
            // R54 · Owner column with inline change. Assignable to any workspace member.
            { key: "enquiry_count", label: "Enq", width: 80, align: "center", sortable: true,
              accessor: (l) => Number(l.enquiry_count || 0),
              render: (l) => {
                const n = Number(l.enquiry_count || 0);
                if (!n) return <span className="text-[10px] text-muted-foreground">—</span>;
                return (
                  <button
                    onClick={(e) => { e.stopPropagation(); setEnqHistoryLead(l); }}
                    className="text-[11px] font-mono border border-emerald-300 bg-emerald-50 text-emerald-900 px-1.5 py-0.5 hover:bg-emerald-100"
                    data-testid={`lead-enq-count-${l.id}`}
                    title={`View ${n} enquir${n === 1 ? "y" : "ies"} from this lead`}
                  >{n}</button>
                );
              },
            },
            // R268pppp · Feb 2026 — Eagerly optimistic reassign.
            // R268xxxx · Feb 2026 — Inline Owner picker REMOVED. The
            // Radix Select's mount/unmount lifecycle kept fighting with
            // MemoRow's memoisation → orphan poppers, stuck overlays,
            // one-click lag. Users now reassign via the row-select
            // "Transfer to…" bulk action, which supports 1..N leads.
            { key: "owner_user_id", label: "Owner", width: 160, sortable: true, groupable: true,
              accessor: (l) => l.owner_name || l.owner_user_id || "",
              render: (l) => {
                const label = l.owner_name || (l.owner_user_id ? `${l.owner_user_id.slice(0, 6)}…` : "Unassigned");
                return (
                  <span
                    className={`text-xs ${l.owner_user_id ? "text-foreground" : "text-muted-foreground italic"}`}
                    data-testid={`lead-owner-${l.id}`}
                    title={l.owner_user_id ? `Owner: ${label}` : "Select rows and click Transfer to assign an owner"}
                  >{label}</span>
                );
              },
            },
            { key: "call", label: "Call", width: 100, align: "center",
              render: (l) => (
                <div className="flex items-center gap-1 justify-center">
                  <button
                    onClick={(e) => { e.stopPropagation(); setCallLogLead(l); }}
                    className="p-1 hover:bg-neutral-100 border border-neutral-200"
                    title="Call log & history"
                    data-testid={`lead-call-log-btn-${l.id}`}
                  >
                    <Phone className="w-3.5 h-3.5 text-sky-600" />
                  </button>
                  {(l.phone || l.mobile || l.office_phone)
                    ? <CallButton phone={l.phone || l.mobile || l.office_phone} customer_name={l.name}
                        entity_type="lead" entity_id={l.id} entity_name={l.name || l.company}
                        className="rounded-none h-7 px-1.5"
                        testid={`lead-call-${l.id}`} />
                    : null}
                </div>
              ),
            },
          ];
          // R293 · Oct 2026 · Branch on view mode. Table + Compact share
          // the AdvancedTable; the Compact pill is wired to the table's
          // built-in density toggle via the `compact` prop. Cards mode
          // switches to the new LeadsCardsView grid.
          if (leadsListMode === "cards" || leadsListMode === "compact") {
            return (
              <LeadsCardsView
                rows={filtered}
                loading={busy && rows.length === 0}
                compact={leadsListMode === "compact"}
                members={wsMembers}
                onRowClick={(l) => setEditing(l)}
                emptyTitle={
                  filterFirst && !hasAnyFilter && rows.length === 0
                    ? "Set filters, then click Apply"
                    : rows.length === 0
                      ? "No leads yet"
                      : "No leads match your filters"
                }
                emptyDescription={
                  filterFirst && !hasAnyFilter && rows.length === 0
                    ? "Pick any filter above (status, owner, search…) and click Load leads."
                    : "Capture prospects from forms, referrals, or outbound campaigns."
                }
              />
            );
          }
          return (
            <AdvancedTable
              tableKey="leads"
              title="Leads"
              rows={filtered}
              columns={columns}
              loading={busy && rows.length === 0}
              emptyIcon={Briefcase}
              // R268eeee · Feb 2026 — When "filter first" is enabled
              // and no filter is picked, show a bespoke prompt so
              // users know they need to Apply. The generic "No leads"
              // wording made new users think the workspace was empty.
              emptyTitle={
                filterFirst && !hasAnyFilter && rows.length === 0
                  ? "Set filters, then click Apply"
                  : rows.length === 0
                    ? "No leads yet"
                    : "No leads match your filters"
              }
              emptyDescription={
                filterFirst && !hasAnyFilter && rows.length === 0
                  ? "Loading every lead on page open slows things down. Pick any filter above (status, owner, search…) and click Load leads."
                  : "Capture prospects from forms, referrals, or outbound campaigns."
              }
              onRowClick={(l) => setEditing(l)}
              selectable={canManageLeads}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              /* R267i · Feb 2026 — Native browser virtualization
                 for the Leads table. Combined with cursor pagination
                 the DOM never has to lay out 5000 rows at once. */
              virtualizeRows={true}
              renderBulkBar={(selRows) => (
                <div className="flex items-center gap-2 text-xs">
                  <Button size="sm" className="rounded-none h-7 text-xs" onClick={() => setShowBulkEdit(true)} data-testid="leads-bulk-edit-btn">
                    <Pencil className="w-3 h-3 mr-1" /> Bulk edit {selRows.length}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setShowBulkTransfer(true)} data-testid="leads-bulk-transfer-btn">
                    <ArrowRight className="w-3 h-3 mr-1" /> Transfer to…
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setShowAddToSegment(true)} data-testid="leads-bulk-add-segment-btn">
                    <Users className="w-3 h-3 mr-1" /> Add to segment
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setShowBulkWa(true)} data-testid="leads-bulk-wa-btn">
                    <MessageCircle className="w-3 h-3 mr-1" /> Bulk WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setShowBulkEmail(true)} data-testid="leads-bulk-email-btn">
                    <Mail className="w-3 h-3 mr-1" /> Bulk Email
                  </Button>
                </div>
              )}
              renderRowActions={(l) => (
                <span className="flex gap-1 justify-end">
                  {(l.phone || l.mobile || l.office_phone) && (
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setWaLead({ ...l, phone: l.phone || l.mobile || l.office_phone }); }} className="rounded-none h-7 px-2 text-xs text-emerald-600" title="WhatsApp chat" data-testid={`lead-wa-${l.id}`}>
                      <MessageCircle className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {!l.client_id && l.status !== "lost" && (
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); convert(l); }} className="rounded-none h-7 px-2 text-xs" title="Convert to client" data-testid={`lead-convert-${l.id}`}>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); del(l); }} className="rounded-none h-7 px-2 text-xs" data-testid={`lead-delete-${l.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </span>
              )}
            />
          );
        })()}
        {/* R267f · Feb 2026 — Cursor pagination "Load more" +
            auto-load sentinel. Once the sentinel is 400 px away
            from the viewport it fires `loadMore`. If the user
            prefers a click, the button above the sentinel does
            the same thing manually. */}
        {(view === "leads" || view === "pipeline") && nextCursor && (
          <LoadMoreSentinel
            onLoadMore={loadMore}
            loading={loadingMore}
            rowsCount={rows.length}
          />
        )}
      </div>

      {editing && (
        <LeadDialog lead={editing} onClose={() => setEditing(null)} onSaved={(saved, skipClose) => {
          // R268e · Feb 2026 — optimistic close + in-memory patch.
          //   • First call from LeadDialog.save() with the merged snapshot
          //     closes the dialog and splices the row instantly.
          //   • A second call with skipClose=true delivers either the
          //     authoritative server response (enrich) or a rollback of
          //     the same row if the PATCH failed.
          if (!skipClose) setEditing(null);
          if (saved && saved.id) {
            setRows((prev) => {
              const idx = prev.findIndex((r) => r.id === saved.id);
              if (idx === -1) {
                // Not on the current page — refresh silently so a new lead surfaces.
                if (!skipClose) load();
                return prev;
              }
              const next = prev.slice();
              next[idx] = { ...next[idx], ...saved };
              return next;
            });
          } else if (!skipClose) {
            load();
          }
        }} industriesCatalog={industriesCatalog} tagCatalog={tagCatalog} leadStatuses={leadStatuses} membersCache={wsMembers}
           onCallLog={(l) => setCallLogLead(l)}
           onOpenWa={(l) => setWaLead(l)} />
      )}
      {/* R294 · Oct 2026 · Mobile filter drawer. Hidden on md+ where
          the inline filter bar is visible. Mirrors the five primary
          filters so users on phones don't have to pinch-zoom through
          a crammed toolbar. Apply / Clear live inside the sheet. */}
      <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
        <SheetContent side="bottom" className="h-[85vh] overflow-y-auto rounded-t-xl p-0" data-testid="leads-filters-sheet">
          <SheetHeader className="p-4 border-b sticky top-0 bg-background z-10">
            <SheetTitle className="text-sm font-semibold uppercase tracking-widest">Filters</SheetTitle>
            <SheetDescription className="text-[11px] text-muted-foreground">
              Pick any combination, then tap Apply. Clear resets every filter.
            </SheetDescription>
          </SheetHeader>
          <div className="p-4 space-y-3">
            <div>
              <Label className="block mb-1 text-[11px] uppercase tracking-widest text-muted-foreground">Status</Label>
              <Select value={filterStatus || "__all__"} onValueChange={(v) => setFilterStatus(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="leads-filter-status-sheet"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="block mb-1 text-[11px] uppercase tracking-widest text-muted-foreground">Source</Label>
              <Select value={filterSource || "__all__"} onValueChange={(v) => setFilterSource(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="leads-filter-source-sheet"><SelectValue placeholder="All sources" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All sources</SelectItem>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="block mb-1 text-[11px] uppercase tracking-widest text-muted-foreground">Industry</Label>
              <Select value={filterIndustry || "__all__"} onValueChange={(v) => { setFilterIndustry(v === "__all__" ? "" : v); setFilterSubIndustry(""); }}>
                <SelectTrigger className="h-10 rounded-none" data-testid="leads-filter-industry-sheet"><SelectValue placeholder="All industries" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="__all__">All industries</SelectItem>
                  {(industriesCatalog.industries || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="block mb-1 text-[11px] uppercase tracking-widest text-muted-foreground">Tag</Label>
              <Select value={filterTag || "__all__"} onValueChange={(v) => setFilterTag(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="leads-filter-tag-sheet"><SelectValue placeholder="All tags" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="__all__">All tags</SelectItem>
                  {tagCatalog.map((t) => <SelectItem key={t.name} value={t.name}>{t.name} ({t.count})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="block mb-1 text-[11px] uppercase tracking-widest text-muted-foreground">Owner</Label>
              <Select value={filterOwner || "__all__"} onValueChange={(v) => setFilterOwner(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="leads-filter-owner-sheet"><SelectValue placeholder="All owners" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="__all__">All owners</SelectItem>
                  <SelectItem value="me">Assigned to me</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {wsMembers.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email || m.user_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="sticky bottom-0 bg-background border-t p-3 flex items-center gap-2">
            <Button variant="outline" size="sm" className="flex-1 rounded-none"
                     onClick={() => { clearFilters(); setFiltersVersion((v) => v + 1); }}
                     data-testid="leads-filters-sheet-clear">
              Clear
            </Button>
            <Button size="sm" className="flex-1 rounded-none"
                     onClick={() => { applyFilters(); setFilterSheetOpen(false); }}
                     data-testid="leads-filters-sheet-apply">
              Apply filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      {/* R291 · Oct 2026 · Mobile-only floating "+" quick-add. Only
          renders on <md viewports and only when the user can create
          leads. Hidden on the Dashboard subtab so the FAB doesn't
          overlap the charts. */}
      <LeadsQuickAddFab
        wsId={activeWorkspaceId}
        sources={SOURCES}
        visible={canManageLeads && (view === "leads" || view === "pipeline")}
        onCreated={(created) => {
          if (created && created.id) {
            setRows((prev) => [created, ...prev]);
          } else {
            load();
          }
        }}
        onOpenFullForm={(draft) => setEditing({ ...(draft || {}) })}
      />
      {showSaveSegment && (
        <SaveSegmentDialog
          onClose={() => setShowSaveSegment(false)}
          filters={currentFilters}
          onSave={saveSegment}
          busy={savingSegment}
        />
      )}
      {showBulkEdit && (
        <BulkEditDialog
          leadIds={[...selectedIds]}
          wsId={activeWorkspaceId}
          industriesCatalog={industriesCatalog}
          leadStatuses={leadStatuses}
          onClose={() => setShowBulkEdit(false)}
          onDone={async () => {
            setShowBulkEdit(false);
            setSelectedIds(new Set());
            await load();
          }}
        />
      )}
      {showBulkTransfer && (
        <BulkTransferDialog
          leadIds={[...selectedIds]}
          wsId={activeWorkspaceId}
          members={wsMembers}
          onClose={() => setShowBulkTransfer(false)}
          onDone={(newOwnerId, newOwnerName) => {
            // R268xxxx · Feb 2026 — Refresh only the transferred rows
            // in place (fastest). No full-list refetch.
            const idSet = new Set([...selectedIds]);
            setRows((prev) => prev.map((r) => idSet.has(r.id)
              ? { ...r, owner_user_id: newOwnerId, owner_name: newOwnerName }
              : r));
            setShowBulkTransfer(false);
            setSelectedIds(new Set());
          }}
        />
      )}
      {showAddToSegment && (
        <AddToSegmentDialog
          leadIds={[...selectedIds]}
          wsId={activeWorkspaceId}
          segments={segments}
          currentFilters={currentFilters}
          onClose={() => setShowAddToSegment(false)}
          onDone={async () => {
            setShowAddToSegment(false);
            setSelectedIds(new Set());
            // Reload segments list
            try {
              const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lead-segments`);
              setSegments(data?.rows || []);
            } catch { /* ignore */ }
          }}
        />
      )}
      {showBulkWa && (
        <BulkWhatsAppDialog
          leadIds={[...selectedIds]}
          wsId={activeWorkspaceId}
          segmentId={selectedIds.size === 0 ? currentSegmentId : null}
          onClose={() => setShowBulkWa(false)}
          onDone={() => {
            setShowBulkWa(false);
            setSelectedIds(new Set());
          }}
        />
      )}
      {showBulkEmail && (
        <BulkEmailDialog
          leadIds={[...selectedIds]}
          wsId={activeWorkspaceId}
          segmentId={selectedIds.size === 0 ? currentSegmentId : null}
          onClose={() => setShowBulkEmail(false)}
          onDone={() => {
            setShowBulkEmail(false);
            setSelectedIds(new Set());
          }}
        />
      )}
      {waLead && <LeadWaChatDialog lead={waLead} onClose={() => setWaLead(null)} />}
      {callLogLead && <LeadCallHistoryDialog lead={callLogLead} open={!!callLogLead} onOpenChange={(v) => { if (!v) setCallLogLead(null); }} />}
      {enqHistoryLead && <LeadEnquiryHistoryDialog lead={enqHistoryLead} open={!!enqHistoryLead} onOpenChange={(v) => { if (!v) setEnqHistoryLead(null); }} />}
      {/* R68 · Workspace lead-status manager */}
      <LeadStatusManagerDialog
        open={showStatusManager}
        onOpenChange={setShowStatusManager}
        wsId={activeWorkspaceId}
        onChange={loadStatuses}
      />
      {/* Oct 2026 · Workspace industry master — inline add/remove plus
          an "Unmatched from imports" section so admins can one-click
          add industry values that leaked in via bulk CSV import. */}
      <IndustryManagerDialog
        open={showIndustryManager}
        onOpenChange={setShowIndustryManager}
        wsId={activeWorkspaceId}
        catalog={industriesCatalog}
        onChange={(fresh) => { if (fresh) setIndustriesCatalog(fresh); else loadIndustries(); }}
      />
    </>
  );
}


// ══════════════════ R267f/g · Feb 2026 · Perf helpers ══════════════════

/**
 * SegmentHealthChip · shows cached lead_count + relative refresh age.
 * Click refreshes the counter on-demand.
 */
function SegmentHealthChip({ seg, wsId, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const count = seg?.lead_count;
  const refreshedAt = seg?.last_refreshed_at;
  const relAge = (() => {
    if (!refreshedAt) return "never";
    const then = new Date(refreshedAt).getTime();
    const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
  })();
  const stale = !refreshedAt || (Date.now() - new Date(refreshedAt).getTime()) > 24 * 3600 * 1000;
  const doRefresh = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/lead-segments/${seg.id}/refresh`);
      onRefresh?.({
        lead_count: data.lead_count,
        last_refreshed_at: data.last_refreshed_at,
      });
      toast.success(`Recounted · ${data.lead_count} lead(s)`);
    } catch (e2) { toast.error(apiErrorMessage(e2, "Refresh failed")); }
    finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={doRefresh}
      disabled={busy}
      className={`text-[9px] font-mono px-1 py-0.5 border-l border-border flex items-center gap-1 ${
        stale ? "text-amber-700 bg-amber-50/60" : "text-muted-foreground"
      } hover:bg-muted disabled:opacity-50`}
      title={refreshedAt
        ? `Nightly refreshed · last recount ${new Date(refreshedAt).toLocaleString()}. Click to refresh now.`
        : "Not yet refreshed. Click to count leads now."}
      data-testid={`lead-segment-health-${seg.id}`}
    >
      {busy ? "…" : count != null ? count : "?"}
      <span className="hidden sm:inline text-[8px] opacity-70">· {relAge}</span>
    </button>
  );
}


/**
 * LoadMoreSentinel · IntersectionObserver-backed "Load more" trigger.
 * Combines a click button (accessible fallback) with auto-load when
 * the sentinel enters the viewport. Guards against duplicate loads.
 */
function LoadMoreSentinel({ onLoadMore, loading, rowsCount }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first?.isIntersecting) onLoadMore();
    }, { rootMargin: "400px 0px" });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLoadMore]);
  return (
    <div
      ref={ref}
      className="mt-2 py-3 flex items-center justify-center border border-dashed border-border text-xs text-muted-foreground bg-muted/30"
      data-testid="leads-load-more-sentinel"
    >
      {loading ? (
        <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading more…</span>
      ) : (
        <button onClick={onLoadMore} className="underline hover:text-foreground"
                data-testid="leads-load-more-btn">
          Showing {rowsCount} leads · load more
        </button>
      )}
    </div>
  );
}


function StatTile({ label, count, subCount = null, active, onClick, testid, color }) {
  return (
    <button
      onClick={onClick}
      className={`border-2 ${active
        ? "border-primary bg-primary/10 ring-2 ring-primary/30 shadow-sm"
        : "border-border hover:border-primary/50"} px-3 py-2 text-left transition-all hover:bg-muted relative`}
      data-testid={testid}
      aria-pressed={active}
    >
      {color && (
        <span className="absolute top-0 left-0 w-1 h-full" style={{ background: color }} aria-hidden="true" />
      )}
      <div className={`label-eyebrow text-[10px] truncate ${active ? "text-primary font-semibold" : ""}`}>{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${active ? "text-primary" : ""}`} style={{ fontFamily: "var(--font-heading)" }}>{count}</div>
      {/* Iter · Feb 2026 — "in filter" secondary count. Only rendered
          when a status other than this tile is active, so users see
          both the true total AND how many are currently visible. */}
      {subCount !== null && subCount !== undefined && subCount !== count && (
        <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5"
             data-testid={`${testid}-in-filter`}>
          <span className="font-semibold text-foreground/70">{subCount}</span> in filter
        </div>
      )}
    </button>
  );
}

function LeadDialog({ lead, onClose, onSaved, industriesCatalog = { industries: [], sub_industries: {} }, tagCatalog = [], leadStatuses = [], membersCache = null, onCallLog, onOpenWa }) {
  const { activeWorkspaceId } = useAuth();
  const isNew = !lead?.id;
  const [researchOpen, setResearchOpen] = useState(false);
  const [members, setMembers] = useState(() => Array.isArray(membersCache) && membersCache.length ? membersCache : []);
  const [form, setForm] = useState({
    name: lead.name || "",
    company: lead.company || "",
    email: lead.email || "",
    phone: lead.phone || "",
    // R138c · Feb 2026 — Extra fields imported from Apollo / Aquarius CRM
    // exports. Surfacing them in the edit dialog so imported leads aren't
    // stranded with data only visible in the raw DB.
    mobile: lead.mobile || "",
    office_phone: lead.office_phone || "",
    secondary_email: lead.secondary_email || "",
    title: lead.title || "",
    department: lead.department || "",
    city: lead.city || "",
    state: lead.state || "",
    country: lead.country || "",
    postal_code: lead.postal_code || "",
    website: lead.website || "",
    linkedin_url: lead.linkedin_url || "",
    lifecycle_stage: lead.lifecycle_stage || "",
    source: lead.source || "other",
    status: lead.status || "new",
    estimated_value: lead.estimated_value || "",
    currency: lead.currency || "INR",
    next_followup_at: lead.next_followup_at || "",
    notes: lead.notes || "",
    close_reason: lead.close_reason || "",
    close_competitor: lead.close_competitor || "",
    close_notes: lead.close_notes || "",
    industry: lead.industry || "",
    sub_industry: lead.sub_industry || "",
    tags: Array.isArray(lead.tags) ? lead.tags : [],
    // R48
    temperature_manual: lead.temperature_manual || "",
    manual_rating: lead.manual_rating ?? null,
    // R64.11 · Owner assignment on Edit-Lead form.
    owner_user_id: lead.owner_user_id || "",
  });
  // Load workspace members for the Owner dropdown.
  // R268e · Feb 2026 — skip the fetch when parent already has them
  // cached (Aquarius' /members payload is ~500 KB, was being re-hit on
  // every lead popup open).
  useEffect(() => {
    if (!activeWorkspaceId) return;
    if (Array.isArray(membersCache) && membersCache.length) {
      setMembers(membersCache);
      return;
    }
    api.get(`/workspaces/${activeWorkspaceId}/members`)
       .then(({ data }) => setMembers(Array.isArray(data) ? data : []))
       .catch(() => setMembers([]));
  }, [activeWorkspaceId, membersCache]);
  // R159v · Feb 2026 — Hydrate heavy text fields (notes, close_notes,
  // close_reason, close_competitor, description) that the list endpoint
  // strips in lean mode. Without this the Edit dialog opens with those
  // fields undefined, Save then overwrites the DB doc with empty strings
  // and the user's notes silently disappear.
  useEffect(() => {
    if (isNew || !lead?.id) return;
    let cancelled = false;
    api.get(`/leads/${lead.id}`)
      .then(({ data }) => {
        if (cancelled || !data) return;
        setForm((s) => ({
          ...s,
          notes: data.notes ?? s.notes,
          close_notes: data.close_notes ?? s.close_notes,
          close_reason: data.close_reason ?? s.close_reason,
          close_competitor: data.close_competitor ?? s.close_competitor,
        }));
      })
      .catch(() => { /* leave form as-is; user can still save non-notes edits */ });
    return () => { cancelled = true; };
  }, [isNew, lead?.id]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // R268uuuu · Feb 2026 — Defer heavy right-column panels
  // (LeadHistoryPanel makes 3-4 API calls, LeadResearchHistory 1) until
  // after the dialog has painted. Users perceive the dialog as instant
  // and the history streams in a moment later. `requestIdleCallback`
  // falls back to a short setTimeout for Safari.
  const [heavyReady, setHeavyReady] = useState(false);
  useEffect(() => {
    if (isNew) { setHeavyReady(true); return; }
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 60));
    const cic = window.cancelIdleCallback || clearTimeout;
    const h = ric(() => setHeavyReady(true), { timeout: 200 });
    return () => cic(h);
  }, [isNew]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      payload.estimated_value = payload.estimated_value === "" ? null : Number(payload.estimated_value);
      if (!payload.email) delete payload.email;
      if (!payload.next_followup_at) payload.next_followup_at = null;
      if (payload.temperature_manual === "") payload.temperature_manual = null;
      if (isNew) {
        // Create needs the server id — can't be truly optimistic; keep
        // the dialog open until the POST resolves.
        const r = await api.post(`/workspaces/${activeWorkspaceId}/leads`, payload);
        const savedLead = (r && r.data) || null;
        toast.success("Lead added");
        await onSaved?.(savedLead);
      } else {
        // R268e · Feb 2026 — TRULY optimistic edit path:
        //  1. Splice merged snapshot into parent list & close dialog
        //  2. Fire PATCH in the background
        //  3. On failure, roll back with an error toast
        const optimistic = { ...lead, ...payload };
        onSaved?.(optimistic);  // closes dialog + patches row immediately
        api.patch(`/leads/${lead.id}`, payload).then((r) => {
          if (payload.status === "won" && lead.status !== "won") {
            celebrate("lead_won", { title: payload.title || payload.name || lead.title || lead.name || "Deal" });
          }
          toast.success("Lead updated");
          // Server may enrich (last_touch_at etc.) — patch again with the
          // authoritative response.
          if (r && r.data) onSaved?.(r.data, /*skipClose*/ true);
        }).catch((e) => {
          toast.error(apiErrorMessage(e, "Save failed — rolled back"));
          // Roll back the row splice to its pre-save state.
          onSaved?.(lead, /*skipClose*/ true);
        });
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  // R295 · Feb 2026 — Expand all / Collapse all pulse. The nonce
  // changes on every click so even "open everything" works twice in
  // a row (useEffect only reacts to a new nonce value).
  const [sectionSignal, setSectionSignal] = useState(null);
  const expandAll = () => setSectionSignal({ open: true,  nonce: Date.now() });
  const collapseAll = () => setSectionSignal({ open: false, nonce: Date.now() });
  const tempVal = lead?.temperature_effective || lead?.temperature_ai || lead?.temperature_manual;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[92vh] rounded-none flex flex-col overflow-hidden p-0" data-testid="lead-dialog">
        <DialogHeader className="px-3 sm:px-6 pt-3 sm:pt-4 pb-2 border-b">
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-2 min-w-0 flex-1 text-base sm:text-lg">
              <span className="truncate">
                {isNew ? "New lead" : `Edit lead · ${lead.name}`}
              </span>
              {!isNew && tempVal && (
                <TemperaturePill value={tempVal} testid="lead-current-temperature" />
              )}
              {!isNew && lead?.on_dnd && (
                <span
                  className="chip text-[10px] bg-rose-50 border-rose-400 text-rose-700 font-semibold px-1.5 py-0.5"
                  title={lead.dnd_reason ? `On DND: ${lead.dnd_reason}` : "This number is on your workspace DND blocklist"}
                  data-testid="lead-profile-dnd-badge"
                >
                  DND
                </span>
              )}
            </DialogTitle>
            {/* Expand / Collapse all — segmented pair sitting flush
                against the X close button. Hidden on `isNew` because a
                brand-new lead starts with every section already open. */}
            {!isNew && (
              <div className="hidden sm:inline-flex items-center border border-border mr-8" data-testid="lead-sections-controls">
                <button type="button" onClick={expandAll}
                        className="px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-muted/40 border-r border-border"
                        data-testid="lead-sections-expand-all">
                  Expand all
                </button>
                <button type="button" onClick={collapseAll}
                        className="px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-muted/40"
                        data-testid="lead-sections-collapse-all">
                  Collapse all
                </button>
              </div>
            )}
          </div>
          {/* Mobile — same pair rendered below the title so it never
              fights the X close button for space on narrow phones. */}
          {!isNew && (
            <div className="sm:hidden mt-2 inline-flex items-center border border-border w-fit" data-testid="lead-sections-controls-mobile">
              <button type="button" onClick={expandAll}
                      className="px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-muted/40 border-r border-border"
                      data-testid="lead-sections-expand-all-mobile">
                Expand all
              </button>
              <button type="button" onClick={collapseAll}
                      className="px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-muted/40"
                      data-testid="lead-sections-collapse-all-mobile">
                Collapse all
              </button>
            </div>
          )}
          {/* Oct 2026 — Quick contact actions mirrored from the leads
              list so a sales rep can call / WhatsApp / email the lead
              right from the Edit dialog without hunting through tabs.
              Hidden on `isNew` since the lead has no contact details
              yet. */}
          {!isNew && (() => {
            const phone = lead?.phone || lead?.mobile || lead?.office_phone;
            const telHref = phone ? `tel:${String(phone).replace(/[^\d+]/g, "")}` : "";
            const waHref = phone
              ? `https://wa.me/${String(phone).replace(/[^\d+]/g, "").replace(/^\+/, "")}`
              : "";
            const mailHref = lead?.email ? `mailto:${lead.email}` : "";
            if (!telHref && !waHref && !mailHref) return null;
            return (
              <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="lead-dialog-actions">
                {telHref && (
                  <>
                    <a href={telHref}
                        className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1 text-xs text-sky-700 hover:bg-sky-50"
                        title={`Call ${phone}`}
                        data-testid="lead-dialog-call-btn">
                      <Phone className="w-3.5 h-3.5" /> Call
                    </a>
                    <button type="button"
                            onClick={() => onCallLog?.(lead)}
                            className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                            title="Call history"
                            data-testid="lead-dialog-call-log-btn">
                      <History className="w-3.5 h-3.5" /> Log
                    </button>
                  </>
                )}
                {waHref && (
                  <button type="button"
                          onClick={() => onOpenWa?.({ ...lead, phone })}
                          className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1 text-xs text-emerald-700 hover:bg-emerald-50"
                          title="Open WhatsApp chat"
                          data-testid="lead-dialog-wa-btn">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </button>
                )}
                {mailHref && (
                  <a href={mailHref}
                      className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1 text-xs text-blue-700 hover:bg-blue-50"
                      title={`Email ${lead.email}`}
                      data-testid="lead-dialog-email-btn">
                    <Mail className="w-3.5 h-3.5" /> Email
                  </a>
                )}
              </div>
            );
          })()}
        </DialogHeader>
        {/* R292 · Oct 2026 — Mobile-first collapsible sections.
            The 20+ fields previously stacked into one scroll-ocean on
            phones; now they're grouped into 6 tap-to-expand sections
            + a Follow-ups panel that defaults open on desktop and
            collapsed on mobile. Group states persist across re-renders
            via local `open` state. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-0">
            {/* Left column — form */}
            <div className="p-3 sm:p-4 lg:border-r lg:overflow-y-auto lg:max-h-[calc(92vh-128px)]">
              <LeadFormSections
                form={form}
                set={set}
                members={members}
                industriesCatalog={industriesCatalog}
                tagCatalog={tagCatalog}
                leadStatuses={leadStatuses}
                isNew={isNew}
                lead={lead}
                signal={sectionSignal}
              />
              {/* R295 · Feb 2026 — Activity + Research live inside the
                  same accordion stack so the spacing rhythm is
                  identical to Basics / Contact / etc. The duplicate
                  desktop right-column still exists in `.hidden lg:block`
                  below, so widescreen users keep the side-by-side
                  layout. */}
              {!isNew && (
                <div className="lg:hidden">
                  <LeadCollapsibleSection
                    title="Activity & follow-ups"
                    defaultOpen={false}
                    testid="lead-section-activity"
                    signal={sectionSignal}
                  >
                    {heavyReady ? (
                      <LeadHistoryPanel leadId={lead.id} leadName={lead.name} onFollowupAdded={onSaved} />
                    ) : (
                      <div className="text-xs text-muted-foreground border border-dashed p-6 text-center" data-testid="lead-history-loading">
                        Loading history…
                      </div>
                    )}
                  </LeadCollapsibleSection>
                </div>
              )}
              {!isNew && lead?.id && heavyReady && (
                <LeadCollapsibleSection
                  title={<><Sparkles className="w-3 h-3 text-primary" /> Research</>}
                  defaultOpen={false}
                  testid="lead-section-research"
                  signal={sectionSignal}
                >
                  <LeadResearchHistory leadId={lead.id} alwaysOpen />
                  <div className="mt-3">
                    <Button type="button" variant="outline" className="rounded-none w-full sm:w-auto"
                            onClick={() => setResearchOpen(true)}
                            data-testid="lead-research-btn">
                      <Sparkles className="w-3.5 h-3.5 mr-1" /> Research further
                    </Button>
                  </div>
                </LeadCollapsibleSection>
              )}
            </div>

            {/* Right column — Follow-ups + blended history. Desktop
                side-by-side only; the mobile stack above already
                includes Activity. */}
            <div className="hidden lg:block p-4 overflow-y-auto lg:max-h-[calc(92vh-128px)] bg-neutral-50/40">
              {isNew ? (
                <div className="text-xs text-muted-foreground border p-8 text-center">
                  Save the lead to unlock the follow-up log and blended call/WhatsApp/email history.
                </div>
              ) : heavyReady ? (
                <LeadHistoryPanel leadId={lead.id} leadName={lead.name} onFollowupAdded={onSaved} />
              ) : (
                <div className="text-xs text-muted-foreground border border-dashed p-6 text-center" data-testid="lead-history-loading">
                  Loading history…
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="border-t px-3 sm:px-6 py-3">
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.name} className="rounded-none" data-testid="lead-save">
            {busy ? "Saving…" : isNew ? "Add lead" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
      {researchOpen && (
        <ResearchDialog open={researchOpen} onClose={() => setResearchOpen(false)}
                        kind="lead" targetId={lead?.id} targetName={lead?.name} />
      )}
    </Dialog>
  );
}

function FormField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid} />
    </div>
  );
}

// Feb 2026 · Lead call history with AI post-call brief inline.
function LeadCallHistory({ leadId, leadName }) {
  const { activeWorkspaceId } = useAuth();
  const [calls, setCalls] = useState([]);
  const [busy, setBusy] = useState(true);
  const [pendingCall, setPendingCall] = useState(null);
  const [briefLang, setBriefLang] = useState({}); // callId -> lang
  const [briefBusy, setBriefBusy] = useState({}); // callId -> bool

  const load = async () => {
    if (!activeWorkspaceId || !leadId) return;
    setBusy(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/calls`,
        { params: { entity_type: "lead", entity_id: leadId, limit: 50 } },
      );
      setCalls(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeWorkspaceId, leadId]);

  const generateBrief = async (call) => {
    const lang = briefLang[call.id] || "english";
    setBriefBusy((s) => ({ ...s, [call.id]: true }));
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/calls/${call.id}/generate-brief`,
        { language: lang },
      );
      toast.success(
        data.stage === "transcribe+analyse"
          ? "Transcribing recording + generating brief…"
          : "Regenerating brief…",
      );
      // Poll the specific call every 4s until analyzed_at updates.
      const before = call.analyzed_at || null;
      const start = Date.now();
      const iv = setInterval(async () => {
        try {
          const { data: c } = await api.get(`/call-logs/${call.id}`);
          if (c && (c.analyzed_at || null) !== before) {
            clearInterval(iv);
            setBriefBusy((s) => ({ ...s, [call.id]: false }));
            load();
          } else if (Date.now() - start > 90_000) {
            clearInterval(iv);
            setBriefBusy((s) => ({ ...s, [call.id]: false }));
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not generate brief"));
      setBriefBusy((s) => ({ ...s, [call.id]: false }));
    }
  };

  if (busy && calls.length === 0) return <div className="text-xs text-muted-foreground">Loading calls…</div>;
  if (!busy && calls.length === 0) {
    return (
      <div className="text-xs text-muted-foreground border border-dashed border-border p-3">
        No calls logged for this lead yet. Click the phone icon in the Leads table to place your first call — MCube will log it here automatically.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="lead-call-history">
      {calls.map((c) => {
        const hasBrief = !!(c.ai_summary || c.ai_next_action || c.ai_key_objection);
        const canBrief = !!(c.recording_url || c.transcript);
        const dur = Number(c.duration_seconds || 0);
        const durLbl = dur > 0 ? `${Math.floor(dur / 60)}m ${dur % 60}s` : "—";
        return (
          <div key={c.id} className="border border-border p-3 space-y-2" data-testid={`lead-call-${c.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold flex items-center gap-2">
                  <span>{c.status || c.dialstatus || "call"}</span>
                  <span className="text-[10px] text-muted-foreground font-normal">
                    · {fmtRelative(c.created_at)} · {durLbl}
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground font-mono">
                  {c.agent_name || "Agent"} ({c.agent_number || "—"}) → {c.customer_number || "—"}
                </div>
              </div>
              {c.recording_url && (
                <audio controls src={c.recording_url} className="h-7 w-48" data-testid={`lead-call-audio-${c.id}`} />
              )}
            </div>

            {hasBrief && (
              <div className="border border-primary/40 bg-primary/5 p-2 text-[11px] space-y-1.5" data-testid={`lead-call-brief-${c.id}`}>
                <div className="text-[9px] label-eyebrow inline-flex items-center gap-1 text-primary">
                  AI brief
                  {c.ai_brief_language && <span className="chip text-[9px] chip-internal ml-1">{c.ai_brief_language}</span>}
                </div>
                {c.ai_summary && <div>{c.ai_summary}</div>}
                <div className="flex flex-wrap items-center gap-1.5">
                  {c.ai_sentiment && (
                    <span className={`chip text-[10px] ${
                      c.ai_sentiment === "positive" ? "chip-approved"
                      : c.ai_sentiment === "negative" ? "chip-rejected"
                      : "chip-status"}`}>{c.ai_sentiment}</span>
                  )}
                  {c.ai_deal_stage && <span className="chip text-[10px] chip-internal">{c.ai_deal_stage}</span>}
                  {c.ai_followup_urgency && c.ai_followup_urgency !== "none" && (
                    <span className={`chip text-[10px] ${c.ai_followup_urgency === "high" ? "chip-rejected" : "chip-pending"}`}>
                      urgency: {c.ai_followup_urgency}
                    </span>
                  )}
                </div>
                {c.ai_next_action && (
                  <div><span className="font-semibold">Next step:</span> {c.ai_next_action}</div>
                )}
                {c.ai_key_objection && (
                  <div><span className="font-semibold">Key objection:</span> {c.ai_key_objection}</div>
                )}
              </div>
            )}

            {!hasBrief && !canBrief && (
              <div className="text-[11px] text-muted-foreground">
                Transcript unavailable — no recording on this call.
              </div>
            )}
            {c.analysis_error && !hasBrief && (
              <div className="border border-rose-400 bg-rose-50/40 p-2 text-[11px] text-rose-800">
                Brief failed: {c.analysis_error}
              </div>
            )}

            {canBrief && (
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={briefLang[c.id] || "english"}
                  onValueChange={(v) => setBriefLang((s) => ({ ...s, [c.id]: v }))}
                >
                  <SelectTrigger className="h-7 text-[11px] w-32 rounded-none" data-testid={`lead-call-brief-lang-${c.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="english">English</SelectItem>
                    <SelectItem value="auto">Auto (transcript)</SelectItem>
                    <SelectItem value="Hindi">Hindi</SelectItem>
                    <SelectItem value="Marathi">Marathi</SelectItem>
                    <SelectItem value="Gujarati">Gujarati</SelectItem>
                    <SelectItem value="Tamil">Tamil</SelectItem>
                    <SelectItem value="Telugu">Telugu</SelectItem>
                    <SelectItem value="Kannada">Kannada</SelectItem>
                    <SelectItem value="Bengali">Bengali</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={() => generateBrief(c)}
                  disabled={!!briefBusy[c.id]}
                  className="rounded-none h-7 text-[11px]"
                  data-testid={`lead-call-generate-brief-${c.id}`}
                >
                  {briefBusy[c.id] ? "Generating…" : (hasBrief ? "Regenerate brief" : "Generate brief")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPendingCall({
                    callId: c.id,
                    agentNumber: c.agent_number,
                    customerNumber: c.customer_number,
                    customerName: leadName,
                    agentName: c.agent_name,
                    initialStatus: c.status || c.dialstatus || "completed",
                  })}
                  className="rounded-none h-7 text-[11px]"
                  data-testid={`lead-call-open-${c.id}`}
                >
                  Details
                </Button>
              </div>
            )}
          </div>
        );
      })}
      {pendingCall && (
        <CallStatusDialog
          {...pendingCall}
          workspaceId={activeWorkspaceId}
          onClose={() => { setPendingCall(null); load(); }}
        />
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid, labelMap }) {
  const displayLabel = (o) => {
    if (o === "__unset__") return "— Choose —";
    if (labelMap && labelMap[o]) return labelMap[o];
    return o || "";
  };
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.filter((o) => o !== "").map((o) => (
            <SelectItem key={o} value={o}>{displayLabel(o)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}


// ─── Iter 199b · Quarterly Win-Loss retro digest panel ────────────────

function WinLossRetroPanel({ wsId }) {
  const [data, setData] = useState(null);
  const [quarter, setQuarter] = useState("current");
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("acq-winloss-retro-collapsed") === "1"; }
    catch { return false; }
  });
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem("acq-winloss-retro-collapsed", next ? "1" : "0"); }
    catch { /* ignore */ }
  };

  useEffect(() => {
    if (!wsId || collapsed) return;
    api.get(`/workspaces/${wsId}/sales/win-loss-retro?quarter=${quarter}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [wsId, quarter, collapsed]);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={toggle}
        className="border border-border bg-muted/30 hover:bg-muted/50 px-3 py-2 mb-3 w-full text-left text-xs font-semibold uppercase tracking-widest flex items-center justify-between"
        data-testid="win-loss-retro-toggle"
      >
        <span>📊 Quarterly Win-Loss retro (click to expand)</span>
        <span className="text-muted-foreground">▼</span>
      </button>
    );
  }

  if (!data) return (
    <div className="border border-border bg-muted/20 px-3 py-4 text-xs mb-3" data-testid="win-loss-retro-loading">
      Loading Win-Loss retro…
    </div>
  );

  const c = data.current;
  const p = data.previous;
  const arrow = (n) => n > 0 ? "▲" : n < 0 ? "▼" : "▬";
  const cls = (n) => n > 0 ? "text-emerald-700" : n < 0 ? "text-red-600" : "text-muted-foreground";

  return (
    <div className="border border-border bg-card mb-3" data-testid="win-loss-retro-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">📊 Win-Loss retro</span>
          <div className="inline-flex border border-border" data-testid="win-loss-retro-quarter">
            {[["current", "This Q"], ["previous", "Last Q"]].map(([v, l]) => (
              <button
                key={v} type="button"
                onClick={() => setQuarter(v)}
                className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${quarter === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                data-testid={`win-loss-retro-${v}`}
              >{l}</button>
            ))}
          </div>
          <span className="text-[11px] text-muted-foreground">
            {data.quarter} vs {p.quarter}
          </span>
        </div>
        <button
          type="button"
          onClick={toggle}
          className="text-muted-foreground hover:text-foreground text-[11px]"
          data-testid="win-loss-retro-collapse"
        >Hide ▲</button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-3">
        <RetroTile label="Win rate" value={`${c.win_rate}%`}
                    delta={data.delta.win_rate} suffix="pp"
                    testid="retro-tile-win-rate" />
        <RetroTile label="Won" value={c.won}
                    delta={data.delta.won} suffix=""
                    testid="retro-tile-won" />
        <RetroTile label="Lost" value={c.lost} testid="retro-tile-lost" />
        <RetroTile label="Won value" value={`${(c.won_value / 1000).toFixed(1)}k`}
                    delta={data.delta.won_value / 1000} suffix="k"
                    testid="retro-tile-won-value" />
        <RetroTile label="Avg deal" value={`${(c.avg_deal_size / 1000).toFixed(1)}k`}
                    testid="retro-tile-avg-deal" />
      </div>

      {/* Reasons + competitors + reps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 px-3 pb-3">
        <div className="border border-border p-2" data-testid="retro-top-wins">
          <div className="label-eyebrow mb-1.5">🏆 Top win reasons</div>
          {c.top_win_reasons.length === 0
            ? <div className="text-[11px] text-muted-foreground italic">No wins captured yet.</div>
            : c.top_win_reasons.slice(0, 5).map((r, i) => (
              <div key={i} className="text-xs flex justify-between py-0.5 border-b border-border/40 last:border-0">
                <span className="truncate">{r.reason.replace(/_/g, " ")}</span>
                <span className="font-mono text-muted-foreground">{r.n}</span>
              </div>
            ))
          }
        </div>
        <div className="border border-border p-2" data-testid="retro-top-losses">
          <div className="label-eyebrow mb-1.5">❌ Top loss reasons</div>
          {c.top_loss_reasons.length === 0
            ? <div className="text-[11px] text-muted-foreground italic">No losses captured yet.</div>
            : c.top_loss_reasons.slice(0, 5).map((r, i) => (
              <div key={i} className="text-xs flex justify-between py-0.5 border-b border-border/40 last:border-0">
                <span className="truncate">{r.reason.replace(/_/g, " ")}</span>
                <span className="font-mono text-muted-foreground">{r.n}</span>
              </div>
            ))
          }
        </div>
        <div className="border border-border p-2" data-testid="retro-competitors">
          <div className="label-eyebrow mb-1.5">🥊 Top competitors</div>
          {c.top_competitors.length === 0
            ? <div className="text-[11px] text-muted-foreground italic">No competitor data captured yet.</div>
            : c.top_competitors.slice(0, 5).map((r, i) => (
              <div key={i} className="text-xs flex justify-between py-0.5 border-b border-border/40 last:border-0">
                <span className="truncate">{r.name}</span>
                <span className="font-mono text-red-600">−{r.n}</span>
              </div>
            ))
          }
        </div>
      </div>

      {/* Rep leaderboard */}
      {c.rep_leaderboard.length > 0 && (
        <div className="border-t border-border px-3 py-2" data-testid="retro-rep-leaderboard">
          <div className="label-eyebrow mb-1.5">🏅 Rep leaderboard</div>
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold border-b border-border/40 pb-1">
            <div className="col-span-5">Owner</div>
            <div className="col-span-2 text-right">Won</div>
            <div className="col-span-2 text-right">Lost</div>
            <div className="col-span-2 text-right">Win rate</div>
            <div className="col-span-1 text-right">Value</div>
          </div>
          {c.rep_leaderboard.slice(0, 5).map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 py-1 text-xs border-b border-border/20 last:border-0">
              <div className="col-span-5 truncate">{i === 0 && "🥇 "}{i === 1 && "🥈 "}{i === 2 && "🥉 "}{r.user_name}</div>
              <div className="col-span-2 text-right font-mono text-emerald-700">{r.won}</div>
              <div className="col-span-2 text-right font-mono text-red-600">{r.lost}</div>
              <div className="col-span-2 text-right font-mono">{r.win_rate}%</div>
              <div className="col-span-1 text-right font-mono text-muted-foreground">{(r.won_value / 1000).toFixed(1)}k</div>
            </div>
          ))}
        </div>
      )}

      {/* QoQ delta strip */}
      <div className="border-t border-border px-3 py-2 flex flex-wrap gap-4 text-[11px] bg-muted/20">
        <span>QoQ Δ:</span>
        <span className={cls(data.delta.win_rate)} data-testid="retro-delta-win-rate">
          {arrow(data.delta.win_rate)} win rate {data.delta.win_rate > 0 ? "+" : ""}{data.delta.win_rate}pp
        </span>
        <span className={cls(data.delta.won)} data-testid="retro-delta-won">
          {arrow(data.delta.won)} won {data.delta.won > 0 ? "+" : ""}{data.delta.won}
        </span>
        <span className={cls(data.delta.won_value)} data-testid="retro-delta-value">
          {arrow(data.delta.won_value)} won value {data.delta.won_value > 0 ? "+" : ""}{(data.delta.won_value / 1000).toFixed(1)}k
        </span>
      </div>
    </div>
  );
}

function RetroTile({ label, value, delta, suffix = "", testid }) {
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "";
  const cls = delta > 0 ? "text-emerald-700" : delta < 0 ? "text-red-600" : "text-muted-foreground";
  return (
    <div className="border border-border p-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground truncate">{label}</div>
      <div className="text-2xl font-black leading-tight" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
      {delta != null && !isNaN(delta) && (
        <div className={`text-[10px] mt-0.5 font-mono ${cls}`}>
          {arrow} {delta > 0 ? "+" : ""}{typeof delta === "number" ? delta.toFixed?.(1) ?? delta : delta}{suffix}
        </div>
      )}
    </div>
  );
}

// ─── Feb 2026 · Tags input (chips + comma parser + suggestions) ─────

function TagsInput({ value = [], onChange, suggestions = [], testid = "tags-input" }) {
  const [draft, setDraft] = useState("");
  const add = (raw) => {
    const parts = String(raw || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = [...value];
    for (const p of parts) {
      if (!next.includes(p)) next.push(p);
    }
    onChange(next);
    setDraft("");
  };
  const remove = (t) => onChange((value || []).filter((x) => x !== t));
  const suggestionsFiltered = (draft
    ? suggestions.filter((s) => s.toLowerCase().includes(draft.toLowerCase()) && !value.includes(s))
    : []
  ).slice(0, 8);

  return (
    <div className="border border-border bg-background px-2 py-1.5 flex flex-wrap gap-1 items-center" data-testid={testid}>
      {(value || []).map((t) => (
        <span key={t} className="chip text-[11px] inline-flex items-center gap-1" data-testid={`tag-chip-${t}`}>
          {t}
          <button type="button" onClick={() => remove(t)} className="text-muted-foreground hover:text-rose-600" aria-label={`Remove tag ${t}`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(draft); }
          else if (e.key === "Backspace" && !draft && value.length) { remove(value[value.length - 1]); }
        }}
        onBlur={() => draft && add(draft)}
        placeholder={value.length ? "" : "Type and press Enter…"}
        className="flex-1 min-w-[120px] bg-transparent text-sm outline-none px-1 py-0.5"
        data-testid={`${testid}-input`}
      />
      {suggestionsFiltered.length > 0 && (
        <div className="w-full border-t border-border mt-1 pt-1 flex flex-wrap gap-1">
          {suggestionsFiltered.map((s) => (
            <button key={s} type="button"
                    onClick={() => add(s)}
                    className="chip text-[10px] hover:bg-primary hover:text-primary-foreground"
                    data-testid={`${testid}-sug-${s}`}>
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Feb 2026 · Save-current-filters-as-segment dialog ─────────

function SaveSegmentDialog({ onClose, filters, onSave, busy }) {
  const [name, setName] = useState("");
  const filterCount = Object.keys(filters || {}).filter((k) => filters[k]).length;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md w-[94vw] max-h-[92vh] overflow-y-auto rounded-none" data-testid="lead-save-segment-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Bookmark className="w-4 h-4 text-primary" />
            Save current filters as segment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Segment name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Hot US SaaS leads"
                    autoFocus
                    className="h-10 rounded-none"
                    data-testid="lead-segment-name" />
          </div>
          <div className="text-xs bg-muted/40 border border-border p-3">
            <div className="label-eyebrow mb-1">Filters captured ({filterCount})</div>
            {filterCount === 0 ? (
              <span className="text-muted-foreground italic">No filters applied — this will match all leads.</span>
            ) : (
              <ul className="space-y-0.5">
                {Object.entries(filters).map(([k, v]) => v ? (
                  <li key={k} className="truncate"><b>{k}</b>: {v}</li>
                ) : null)}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={() => onSave(name)} disabled={busy || !name.trim()} data-testid="lead-segment-save">
            {busy ? "Saving…" : "Save segment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// R268xxxx · Feb 2026 — Bulk transfer selected leads to a new owner.
// Replaces the removed inline Owner Select. Handles 1..N leads.
function BulkTransferDialog({ leadIds, wsId, members = [], onClose, onDone }) {
  const [ownerId, setOwnerId] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const visible = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const list = (members || []).filter((m) => m.status !== "removed");
    if (!qq) return list;
    return list.filter((m) =>
      (m.name || "").toLowerCase().includes(qq) ||
      (m.email || "").toLowerCase().includes(qq));
  }, [members, q]);
  const submit = async () => {
    if (!ownerId) { toast.error("Pick a user"); return; }
    setBusy(true);
    try {
      const newOwner = ownerId === "__none__" ? null : ownerId;
      await api.post(`/workspaces/${wsId}/leads/bulk-update`, {
        lead_ids: leadIds,
        patch: { owner_user_id: newOwner },
      });
      const ownerRow = newOwner ? members.find((m) => m.user_id === newOwner) : null;
      const ownerName = ownerRow ? (ownerRow.name || ownerRow.email || newOwner) : null;
      toast.success(newOwner
        ? `Transferred ${leadIds.length} lead${leadIds.length === 1 ? "" : "s"} to ${ownerName}`
        : `Unassigned ${leadIds.length} lead${leadIds.length === 1 ? "" : "s"}`);
      onDone?.(newOwner, ownerName);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Transfer failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="lead-bulk-transfer-dialog">
        <DialogHeader>
          <DialogTitle>Transfer {leadIds.length} lead{leadIds.length === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="block mb-1.5 text-xs uppercase tracking-widest text-muted-foreground">New owner</Label>
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search users…"
                className="h-9 pl-8 rounded-none"
                data-testid="lead-bulk-transfer-search"
              />
            </div>
            <div className="border border-border max-h-72 overflow-y-auto">
              <button
                type="button"
                onClick={() => setOwnerId("__none__")}
                className={`w-full text-left px-3 py-2 text-xs border-b border-border/60 flex items-center gap-2 hover:bg-muted/40 ${ownerId === "__none__" ? "bg-primary/10 text-primary" : ""}`}
                data-testid="lead-bulk-transfer-opt-unassigned"
              >
                <span className="italic text-muted-foreground">Unassigned</span>
              </button>
              {visible.length === 0 && (
                <div className="px-3 py-6 text-xs text-muted-foreground text-center italic">No matching users</div>
              )}
              {visible.map((m) => (
                <button
                  key={m.user_id}
                  type="button"
                  onClick={() => setOwnerId(m.user_id)}
                  className={`w-full text-left px-3 py-2 text-xs border-b border-border/60 hover:bg-muted/40 ${ownerId === m.user_id ? "bg-primary/10 text-primary" : ""}`}
                  data-testid={`lead-bulk-transfer-opt-${m.user_id}`}
                >
                  <div className="font-medium">{m.name || m.email || m.user_id}</div>
                  {m.email && m.name && <div className="text-[10px] text-muted-foreground">{m.email}</div>}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="lead-bulk-transfer-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || !ownerId} data-testid="lead-bulk-transfer-submit">
            {busy ? "Transferring…" : "Transfer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ─── Feb 2026 · Bulk edit multiple leads ───────────────────────

function BulkEditDialog({ leadIds, wsId, industriesCatalog, onClose, onDone, leadStatuses = [] }) {
  const [patch, setPatch] = useState({});
  const [tagsAdd, setTagsAdd] = useState([]);
  const [tagsRemove, setTagsRemove] = useState([]);
  const [busy, setBusy] = useState(false);
  const setP = (k, v) => setPatch((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    // Strip empty / unchanged fields
    const clean = {};
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      clean[k] = v;
    });
    if (Object.keys(clean).length === 0 && tagsAdd.length === 0 && tagsRemove.length === 0) {
      toast.error("Nothing to update — pick at least one field or tag change");
      return;
    }
    if (!confirm(`Apply these changes to ${leadIds.length} lead(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/leads/bulk-update`, {
        lead_ids: leadIds,
        patch: clean,
        tags_add: tagsAdd,
        tags_remove: tagsRemove,
      });
      toast.success(`Updated ${data.updated || leadIds.length} lead(s)`);
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk update failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl w-[94vw] max-h-[92vh] overflow-y-auto rounded-none" data-testid="lead-bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Pencil className="w-4 h-4 text-primary" />
            Bulk edit {leadIds.length} lead(s)
          </DialogTitle>
        </DialogHeader>
        <div className="text-[11px] text-muted-foreground italic mb-3">
          Only the fields you fill in will be updated. Leave a field empty to keep existing values.
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Status</Label>
              <Select value={patch.status || "__keep__"} onValueChange={(v) => setP("status", v === "__keep__" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="bulk-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__keep__">— Keep existing —</SelectItem>
                  {(leadStatuses.length ? leadStatuses.filter(s => !s.archived_at).map(s => ({ k: s.key, l: s.label })) : STATUSES.map(k => ({ k, l: k }))).map((o) => (
                    <SelectItem key={o.k} value={o.k}>{o.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Source</Label>
              <Select value={patch.source || "__keep__"} onValueChange={(v) => setP("source", v === "__keep__" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="bulk-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__keep__">— Keep existing —</SelectItem>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Industry</Label>
              <SearchableSelect
                value={patch.industry || ""}
                onValueChange={(v) => setP("industry", v || "")}
                options={industriesCatalog.industries || []}
                placeholder="— Keep existing —"
                testid="bulk-industry"
              />
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Sub-industry</Label>
              <SearchableSelect
                value={patch.sub_industry || ""}
                onValueChange={(v) => setP("sub_industry", v || "")}
                options={industriesCatalog.sub_industries?.[patch.industry] || []}
                placeholder={patch.industry ? "— Keep existing —" : "Pick industry above first"}
                disabled={!patch.industry}
                testid="bulk-sub-industry"
              />
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Currency</Label>
              <Input value={patch.currency || ""} onChange={(e) => setP("currency", e.target.value)} placeholder="Keep existing" className="h-10 rounded-none" data-testid="bulk-currency" />
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Next follow-up date</Label>
              <Input type="date" value={patch.next_followup_at || ""} onChange={(e) => setP("next_followup_at", e.target.value)} className="h-10 rounded-none" data-testid="bulk-followup" />
            </div>
            <div className="col-span-2">
              <Label className="block mb-1.5 text-sm font-semibold">Notes (replaces existing if set)</Label>
              <Textarea value={patch.notes || ""} onChange={(e) => setP("notes", e.target.value)} rows={2} placeholder="Leave blank to keep existing notes" data-testid="bulk-notes" />
            </div>
          </div>

          <div className="border border-border bg-muted/30 p-3 space-y-2">
            <div className="label-eyebrow inline-flex items-center gap-1">
              <TagIcon className="w-3 h-3" /> Tag operations
            </div>
            <div>
              <Label className="block mb-1 text-[11px] font-semibold">Add these tags to all selected</Label>
              <TagsInput value={tagsAdd} onChange={setTagsAdd} testid="bulk-tags-add" />
            </div>
            <div>
              <Label className="block mb-1 text-[11px] font-semibold">Remove these tags from all selected</Label>
              <TagsInput value={tagsRemove} onChange={setTagsRemove} testid="bulk-tags-remove" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="lead-bulk-save">
            {busy ? "Applying…" : `Apply to ${leadIds.length} lead(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ─────────────────────────────────────────────────────────────
// Iter Feb 2026 R36 · Inline 1-5 star rating cell for the Leads table.
// Optimistic update — the row lifts to the current rating immediately;
// the API roundtrip is silent (no toast on success) so bulk-rating a
// column of leads feels instant. On failure we revert and toast the
// error.
// ─────────────────────────────────────────────────────────────
function LeadRatingCell({ lead, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(0);
  const current = Number(lead.manual_rating || 0);
  const setRating = async (n) => {
    const next = n === current ? null : n; // click same star → clear
    setBusy(true);
    // Optimistic
    onChanged?.(next);
    try {
      await api.patch(`/leads/${lead.id}`, { manual_rating: next });
    } catch (e) {
      onChanged?.(current); // revert
      toast.error(apiErrorMessage(e, "Rating update failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="inline-flex items-center gap-0.5"
      onMouseLeave={() => setHover(0)}
      data-testid={`lead-rating-${lead.id}`}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = hover ? n <= hover : n <= current;
        return (
          <button
            key={n}
            type="button"
            disabled={busy}
            onMouseEnter={() => setHover(n)}
            onClick={() => setRating(n)}
            className="h-5 w-5 inline-flex items-center justify-center p-0 disabled:opacity-50"
            aria-label={`Rate ${n} star${n === 1 ? "" : "s"}`}
            data-testid={`lead-rating-${lead.id}-star-${n}`}
          >
            <Star
              className={`w-3.5 h-3.5 ${filled ? "fill-amber-400 text-amber-500" : "text-muted-foreground/40"}`}
            />
          </button>
        );
      })}
    </div>
  );
}

// R64.11 · <LeadResearchHistory /> — collapsible list of past AI-research
// runs saved when the "Research further" dialog was submitted. Each row
// renders the query + timestamp + expand to show the full summary. Users
// can scroll historical research inline without leaving the Edit-Lead
// dialog. Data source: GET /workspaces/{ws}/research/history/lead/{id}.
function _LeadResearchHistory({ leadId, alwaysOpen = false }) {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(alwaysOpen);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!open || !activeWorkspaceId || !leadId) return;
    setLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/research/history/lead/${leadId}`)
       .then(({ data }) => setRows(data?.rows || []))
       .catch(() => setRows([]))
       .finally(() => setLoading(false));
  }, [open, activeWorkspaceId, leadId]);

  return (
    <div data-testid="lead-research-history">
      {!alwaysOpen && (
        <button type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between text-[11px] uppercase tracking-widest font-semibold text-muted-foreground hover:text-foreground"
                data-testid="lead-research-history-toggle">
          <span className="inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-primary" /> Research history {rows.length > 0 ? `· ${rows.length}` : ""}
          </span>
          <span>{open ? "▾" : "▸"}</span>
        </button>
      )}
      {alwaysOpen && (
        <div className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground mb-2 inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-primary" /> Research history {rows.length > 0 ? `· ${rows.length}` : ""}
        </div>
      )}
      {open && (
        <div className="mt-2 space-y-1.5">
          {loading && <div className="text-xs text-muted-foreground italic">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-xs text-muted-foreground italic border border-dashed border-border p-2">
              No research yet — click <b>Research further</b> below to run your first AI research on this lead.
            </div>
          )}
          {rows.map((r) => {
            const isExp = expandedId === r.id;
            const ts = (r.created_at || "").slice(0, 16).replace("T", " ");
            const q = r.query || r.prompt || "(no query)";
            const summary = r.summary || r.result || r.answer || "";
            return (
              <div key={r.id} className="border border-border" data-testid={`lead-research-row-${r.id}`}>
                <button type="button"
                        onClick={() => setExpandedId(isExp ? null : r.id)}
                        className="w-full text-left px-2 py-1.5 hover:bg-muted/40 flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{q}</div>
                    <div className="text-[10px] text-muted-foreground">{ts}{r.user_name ? ` · ${r.user_name}` : ""}</div>
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">{isExp ? "▾" : "▸"}</span>
                </button>
                {isExp && (
                  <div className="px-2 py-1.5 border-t border-border/60 text-[11px] whitespace-pre-wrap break-words bg-muted/20 max-h-72 overflow-y-auto"
                       data-testid={`lead-research-body-${r.id}`}
                       dangerouslySetInnerHTML={{ __html: summary }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



// R268e · Feb 2026 — memoise so LeadDialog keystrokes stop re-rendering
// this panel. `leadId` is the only prop that actually changes state.
const LeadResearchHistory = memo(
  _LeadResearchHistory,
  (prev, next) => prev.leadId === next.leadId && prev.alwaysOpen === next.alwaysOpen,
);


// Feb 2026 · Lead → Enquiry history drawer
function LeadEnquiryHistoryDialog({ lead, open, onOpenChange }) {
  const [state, setState] = useState({ loading: true, enquiries: [], count: 0 });
  useEffect(() => {
    if (!open || !lead?.id) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/leads/${lead.id}/enquiries`);
        if (alive) setState({ loading: false,
                                enquiries: r.data?.enquiries || [],
                                count: r.data?.count || 0 });
      } catch (e) {
        if (alive) setState({ loading: false, enquiries: [], count: 0 });
      }
    })();
    return () => { alive = false; };
  }, [open, lead?.id]);

  const chipCls = (status) => {
    const s = (status || "").toLowerCase();
    if (["won", "closed_won"].includes(s)) return "bg-emerald-100 text-emerald-900 border-emerald-300";
    if (["lost", "closed_lost"].includes(s)) return "bg-rose-100 text-rose-900 border-rose-300";
    if (["quoted", "contacted", "qualified"].includes(s)) return "bg-blue-100 text-blue-900 border-blue-300";
    return "bg-neutral-100 text-neutral-800 border-neutral-300";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto p-0" data-testid="lead-enq-history-dialog">
        <DialogHeader className="p-4 border-b border-border sticky top-0 bg-background z-10">
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>Enquiry history</span>
            <span className="text-xs font-normal text-muted-foreground">— {lead?.name}</span>
            <span className="ml-auto text-[10px] font-mono border border-emerald-300 bg-emerald-50 text-emerald-900 px-1.5 py-0.5"
                   data-testid="lead-enq-history-count">{state.count} total</span>
          </DialogTitle>
        </DialogHeader>
        <div className="p-4 space-y-2" data-testid="lead-enq-history-list">
          {state.loading ? (
            <div className="text-xs text-muted-foreground italic">Loading…</div>
          ) : state.enquiries.length === 0 ? (
            <div className="text-xs text-muted-foreground italic text-center py-6">No enquiries linked to this lead yet.</div>
          ) : state.enquiries.map((e) => (
            <a key={e.id} href={`/app/enquiries?focus=${e.id}`}
                className="block border border-border p-3 hover:border-primary transition-colors"
                data-testid={`lead-enq-history-item-${e.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] bg-neutral-100 border border-neutral-300 px-1.5 py-0.5">{e.code}</span>
                <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${chipCls(e.status)}`}>{e.status}</span>
                <span className="text-xs font-semibold">{e.type_name}</span>
                {e.subtype_slug && <span className="text-[10px] text-muted-foreground">· {e.subtype_slug}</span>}
                <span className="ml-auto text-[10px] text-muted-foreground">{(e.created_at || "").slice(0, 10)}</span>
              </div>
              {e.message_snippet && (
                <div className="text-xs mt-1.5 text-muted-foreground line-clamp-2">{e.message_snippet}</div>
              )}
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1.5 flex-wrap">
                {e.assignee_name && <span>Assignee: <b className="text-foreground">{e.assignee_name}</b></span>}
                {e.budget && <span>Budget: <b className="text-foreground">{e.budget}</b></span>}
                {e.source && <span>Source: <b className="text-foreground">{e.source}</b></span>}
              </div>
            </a>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// R292 · Oct 2026 — Collapsible section for the Edit Lead dialog on
// mobile. Uses a `<details>`-style disclosure under the hood (native
// animations, keyboard-accessible, no extra deps). Each section's
// open state is controlled so the parent can decide which group
// defaults open per viewport.
function LeadCollapsibleSection({ title, defaultOpen = false, children, testid, signal }) {
  const [open, setOpen] = useState(defaultOpen);
  // When the parent fires an expand-all / collapse-all pulse, the
  // signal nonce changes and every section snaps to that state. The
  // user can still toggle individual sections afterwards.
  useEffect(() => {
    if (!signal) return;
    setOpen(!!signal.open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signal?.nonce]);
  return (
    <div className="border border-border bg-background mb-2" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-muted/40 active:bg-muted/60 transition-colors"
        aria-expanded={open}
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        <span className="text-xs font-semibold uppercase tracking-widest text-foreground inline-flex items-center gap-1.5">
          {title}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-border/60" data-testid={testid ? `${testid}-body` : undefined}>
          {children}
        </div>
      )}
    </div>
  );
}

// R292 · Oct 2026 — All Lead form fields grouped into 6 collapsible
// sections. "Basics" starts open; everything else collapsed so the
// user sees a compact set of 6 fields + 5 accordions on first open
// instead of a 25-field scroll-ocean.
function LeadFormSections({ form, set, members, industriesCatalog, tagCatalog, leadStatuses, isNew, lead, signal }) {
  return (
    <div className="space-y-0">
      <LeadCollapsibleSection title="Basics" defaultOpen testid="lead-section-basics" signal={signal}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="lead-name" />
          <FormField label="Company" value={form.company} onChange={(v) => set("company", v)} testid="lead-company" />
          <FormField label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testid="lead-email" />
          <FormField label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="lead-phone" />
          <SelectField
            label="Status"
            value={form.status}
            onChange={(v) => set("status", v)}
            options={leadStatuses.length ? leadStatuses.filter((s) => !s.archived_at).map((s) => s.key) : STATUSES}
            labelMap={leadStatuses.length ? Object.fromEntries(leadStatuses.map((s) => [s.key, s.label])) : null}
            testid="lead-status"
          />
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Owner</Label>
            <Select value={form.owner_user_id || "__none__"}
                      onValueChange={(v) => set("owner_user_id", v === "__none__" ? "" : v)}>
              <SelectTrigger className="h-10 rounded-none" data-testid="lead-owner"><SelectValue placeholder="— Unassigned —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Unassigned —</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.user_id || m.id} value={m.user_id || m.id} data-testid={`lead-owner-opt-${m.user_id || m.id}`}>
                    {m.name || m.email || (m.user_id || m.id)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </LeadCollapsibleSection>

      <LeadCollapsibleSection title="Contact details" testid="lead-section-contact" signal={signal}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="Secondary email" type="email" value={form.secondary_email} onChange={(v) => set("secondary_email", v)} testid="lead-secondary-email" />
          <FormField label="Mobile" value={form.mobile} onChange={(v) => set("mobile", v)} testid="lead-mobile" />
          <FormField label="Office phone" value={form.office_phone} onChange={(v) => set("office_phone", v)} testid="lead-office-phone" />
          <FormField label="Title" value={form.title} onChange={(v) => set("title", v)} testid="lead-title" />
          <FormField label="Department" value={form.department} onChange={(v) => set("department", v)} testid="lead-department" />
          <FormField label="Website" value={form.website} onChange={(v) => set("website", v)} testid="lead-website" />
          <FormField label="LinkedIn URL" value={form.linkedin_url} onChange={(v) => set("linkedin_url", v)} testid="lead-linkedin" />
        </div>
      </LeadCollapsibleSection>

      <LeadCollapsibleSection title="Location" testid="lead-section-location" signal={signal}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="City" value={form.city} onChange={(v) => set("city", v)} testid="lead-city" />
          <FormField label="State" value={form.state} onChange={(v) => set("state", v)} testid="lead-state" />
          <FormField label="Country" value={form.country} onChange={(v) => set("country", v)} testid="lead-country" />
          <FormField label="Postal code" value={form.postal_code} onChange={(v) => set("postal_code", v)} testid="lead-postal-code" />
        </div>
      </LeadCollapsibleSection>

      <LeadCollapsibleSection title="Classification" testid="lead-section-classification" signal={signal}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SelectField label="Source" value={form.source} onChange={(v) => set("source", v)} options={SOURCES} testid="lead-source" />
          <FormField label="Lifecycle stage" value={form.lifecycle_stage} onChange={(v) => set("lifecycle_stage", v)} testid="lead-lifecycle-stage" />
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Temperature (manual)</Label>
            <Select value={form.temperature_manual || "__auto__"} onValueChange={(v) => set("temperature_manual", v === "__auto__" ? "" : v)}>
              <SelectTrigger className="h-10 rounded-none" data-testid="lead-temperature-manual"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">— Auto (AI) —</SelectItem>
                <SelectItem value="hot">Hot</SelectItem>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="cold">Cold</SelectItem>
              </SelectContent>
            </Select>
            {!isNew && lead.temperature_ai && (
              <div className="text-[10px] text-muted-foreground mt-1">AI suggestion: <span className="uppercase font-semibold">{lead.temperature_ai}</span></div>
            )}
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Industry</Label>
            <SearchableSelect
              value={form.industry}
              onValueChange={(v) => { set("industry", v || ""); if (v !== form.industry) set("sub_industry", ""); }}
              options={industriesCatalog.industries || []}
              placeholder="— Select industry —"
              clearable
              width="100%"
              triggerClassName="w-full h-10"
              testid="lead-industry"
            />
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Sub-industry</Label>
            <SearchableSelect
              value={form.sub_industry}
              onValueChange={(v) => set("sub_industry", v || "")}
              options={industriesCatalog.sub_industries?.[form.industry] || []}
              placeholder={form.industry ? "— Select sub-industry —" : "Pick industry first"}
              disabled={!form.industry}
              clearable
              width="100%"
              triggerClassName="w-full h-10"
              testid="lead-sub-industry"
            />
          </div>
        </div>
        <div className="mt-3">
          <Label className="block mb-1.5 text-sm font-semibold">Tags <span className="text-[10px] text-muted-foreground font-normal">(Hot, VIP, Cold, etc.)</span></Label>
          <TagsInput value={form.tags} onChange={(v) => set("tags", v)} suggestions={tagCatalog.map((t) => t.name)} testid="lead-tags" />
        </div>
      </LeadCollapsibleSection>

      <LeadCollapsibleSection title="Deal" testid="lead-section-deal" signal={signal}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="Estimated value" type="number" value={form.estimated_value} onChange={(v) => set("estimated_value", v)} testid="lead-value" />
          <FormField label="Currency" value={form.currency} onChange={(v) => set("currency", v)} testid="lead-currency" />
          <FormField label="Next follow-up" type="date" value={form.next_followup_at?.slice(0, 10) || ""} onChange={(v) => set("next_followup_at", v)} testid="lead-followup" />
        </div>
      </LeadCollapsibleSection>

      <LeadCollapsibleSection
        title={(form.status === "won" || form.status === "lost") ? `Notes + ${form.status === "won" ? "Win" : "Loss"} capture` : "Notes"}
        defaultOpen={form.status === "won" || form.status === "lost"}
        testid="lead-section-notes"
        signal={signal}
      >
        <div>
          <Label className="block mb-1.5 text-sm font-semibold">Notes</Label>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="rounded-none" data-testid="lead-notes" />
        </div>
        {(form.status === "won" || form.status === "lost") && (
          <div className="border-2 border-primary/30 bg-primary/5 p-3 space-y-3 mt-3" data-testid="win-loss-capture">
            <div className="label-eyebrow flex items-center gap-2">
              <span>{form.status === "won" ? "🏆 Win capture" : "❌ Loss capture"}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SelectField
                label={form.status === "won" ? "Why did we win?" : "Why did we lose?"}
                value={form.close_reason || "__unset__"}
                onChange={(v) => set("close_reason", v === "__unset__" ? "" : v)}
                options={["__unset__", "price", "features", "timing", "competitor",
                           "no_budget", "no_need", "poor_fit", "champion_left",
                           "referral", "brand", "sales_process",
                           "product_quality", "support", "other"]}
                testid="lead-close-reason"
              />
              {form.status === "lost" && (
                <FormField
                  label="Competitor (if any)"
                  value={form.close_competitor}
                  onChange={(v) => set("close_competitor", v)}
                  testid="lead-close-competitor"
                />
              )}
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Close notes</Label>
              <Textarea value={form.close_notes}
                          onChange={(e) => set("close_notes", e.target.value)}
                          rows={2}
                          className="rounded-none"
                          data-testid="lead-close-notes" />
            </div>
          </div>
        )}
      </LeadCollapsibleSection>
    </div>
  );
}

